import React, { useEffect, useRef, useState, useCallback } from "react";
import { approvedView, soaApplicabilityName, soaStatusName, type SoaApprovalEntry } from "../lib/soaApproval";
import { useNavCountsContext } from "../nav/NavCountsContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AlertTriangle, CheckCircle2 } from "lucide-react";
import { clientPortalPath } from "../nav/consultantNav";
import { frameworkColor, frameworkLabel } from "../nav/frameworkMeta";
import { controlRefWithStandard, displayControlRef, displayControlTitle } from "../lib/controlRef";
import { geographyLabel } from "../lib/geography";
import { rankActions } from "./ActionsPage";
import { theme, card, emptyFrame, btnPrimary, btnSecondary, inputStyle } from "../theme";
import { IndexTabs, RulerProgress, StatusTag, type StatusTone } from "../components/Drawing";
import { ControlComments } from "../components/ControlComments";
import { StandardLicencesPanel } from "../components/StandardLicencesPanel";
import { api, apiErrorMessage } from "../api/client";
import { useCurrentUser } from "../auth/useCurrentUser";
import { fmtDate } from "../utils/format";
import { allocationClearPrompt, consultancyAllocationPence, deliveryDifferenceNote } from "../lib/pricing";
import { scoreColor, scoreLabel } from "./consultant/types";
import { ClientSummaryStrip } from "./consultant/ClientSummaryStrip";
import { EngagementNotesTab } from "./consultant/EngagementNotes";
import { ViewAsPersonaPicker, viewAsPersona } from "./consultant/ViewAsPersona";
import { FRAMEWORK_LABELS, type FrameworkId } from "../context/FrameworksContext";
import { ESG_FRAMEWORK_IDS, FRAMEWORK_ORDER, UNPAID_FRAMEWORK_IDS } from "../lib/frameworkCatalogue";
import {
  PACKAGES, packageOf, gbp, gbpMo, TIER_ADDON_PENCE,
  TPRM_ADDON_PENCE, DORA_MONITORING_ADDON_PENCE, ESG_ADDON_PENCE, AUDITOR_ACCESS_ADDON_PENCE, JURISDICTION_ADDON_PENCE,
  PAYG_PEOPLE_MAX, SCALING_CEILING_PENCE, SCALING_CONTRACT_NOTES, ENDPOINTS_INCLUDED,
  peopleScalingLabel, endpointScalingLabel, type PeopleScalingRule,
} from "../lib/pricing";

// ── Types ─────────────────────────────────────────────────────────────────

interface Bureau {
  id: string;
  name: string;
  accreditation: string | null;
  country: string | null;
  // Standards the bureau is accredited for, and its price per standard.
  frameworkIds: string[];
  prices: Array<{ frameworkId: string; certFeePence: number; annualFeePence: number }>;
}

// One certification bureau per standard, each with its own contract lock.
interface Engagement {
  frameworkId: string;
  bureau: { id: string; name: string; accreditation: string | null; country: string | null };
  selectedAt: string;
  lockedUntil: string | null;
  locked: boolean;
}

interface ServiceTier {
  id: string;
  name: string;
  level: number;
  basePlatformFeePence: number;
}

interface PendingTierRequest {
  id: string;
  requestedTierId: string;
  requestedTier: { name: string };
  note: string | null;
  requestedAt: string;
}

interface ClientDetail {
  id: string;
  name: string;
  isDemo?: boolean;
  sector: string | null;
  size: string | null;
  geography: string | null;
  ciso: string | null;
  ismsScope: string | null;
  serviceTier: ServiceTier;
  engagements: Engagement[];
  apiFrequency: string;
  spendCapPence: number;
  consultancyAiCheckCap: number | null;
  aiChecksThisPeriod: number;
  aiCheckPeriodResetsAt: string | null;
  hasConsultancyAi: boolean;
  consultancyDaysPerMonth: number;
  consultancyHoursRemaining: number;
  billingMode: string;
  managedServiceRatePence: number | null;
  managedServiceGrade: string | null;
  managedServiceDaysPerMonth: number;
  managedServiceHoursPerMonth: number;
  soaStats: { compliant: number; partial: number; gap: number };
  // Per standard, on the client Dashboard's denominators (backend lib/frameworkScores.ts).
  frameworkStats: Array<{
    frameworkId: string;
    score: number | null;
    mandatoryCompliant: number; mandatoryTotal: number;
    annexCompliant: number; annexTotal: number;
    compliant: number; partial: number; gap: number; total: number;
  }>;
  ceCompliant: number;
  /** The Cyber Essentials requirement rows the score is over (shared by CE and CE+). */
  ceTotal: number;
  /** The client's SOA score — what their own sidebar shows. */
  latestScore: number | null;
  /** The last AI compliance check's overall score. */
  lastCheckScore: number | null;
  lastChecked: string | null;
  openFindings: number;
  pendingTierRequest: PendingTierRequest | null;
  monthlyBilledPence: number;
  monthlyAbacasRevenuePence: number;
  /** The consultancy allocation (days x rate): what the client is billed for consultancy time. */
  monthlyConsultancyRevenuePence: number;
  /** Confirmed bookings and logged time this month, beside the allocation. */
  monthlyConsultancyDeliveredPence: number;
  consultancyDeliveryDifferencePence: number;
  consultancyDeliveryFlag: "none" | "below" | "above";
  monthlyAiSpendPence: number;
  frameworks: Array<{ frameworkId: string; enrolledAt: string }>;
  allBureaux: Bureau[];
  allTiers: ServiceTier[];
  tprmEnabled: boolean;
  doraMonitoringEnabled: boolean;
  /** DORA reaches this client (EU / EEA financial entity); the add-on can be switched on only if so. */
  doraApplies: boolean;
  esgEnabled: boolean;
  auditorAccessEnabled: boolean;
  legalObligationsEnabled: boolean;
  /** People in scope agreed in the contract at purchase (null = not pinned; the declared size is read instead). */
  contractedHeadcount: number | null;
  headcountBasis: { billed: number | null; contracted: number | null; directory: number | null; extra: number; basis: "contract" | "size" | "directory" | "none" };
  smallBusinessPackage: boolean;
  bundleTier: string | null;
  hardeningSubscriptionCount?: number;
  jurisdictionCount?: number;
  /** The package the organisation is on and what it entitles it to; null on pay-as-you-go. */
  /** The product the client is on (backend lib/editions.ts); a Jersey client signs in at jsy.abacassecurity.com. */
  edition?: "standard" | "jersey";
  /** The Jersey package's term, on the Jersey edition only. */
  jerseyPackage?: {
    annualPence: number; monthlyPence: number; termMonths: number; consultancyHoursByYear: number[];
    billingInterval: "monthly" | "annual"; certificateAnnualPence: number; cepAnnualPence: number;
    termStartsAt: string | null; termEndsAt: string | null; termYear: number | null;
  } | null;
  package: {
    id: "ce_annual" | "jersey" | "small" | "medium" | "large" | "enterprise";
    name: string;
    serviceTierId: string;
    tierLevel: number;
    tierFixed: boolean;
    includedFrameworkIds: string[];
    allowedFrameworkIds: string[] | null;
    addons: { tprm: boolean; dora: boolean; esg: boolean };
    tieredStandardAllowance: number | null;
    jurisdictionAllowance: number | null;
    aiCreditPence: number;
  } | null;
  // Every platform line, from the same computation that bills the client (backend lib/pricing.ts).
  billingBreakdown: BillingBreakdown;
}

interface BillingBreakdown {
  model: "small_business" | "bundle" | "enterprise" | "payg" | "jersey" | "jersey_licensed";
  standardsFeePence: number;
  tierAddonPence: number;
  integrationAddonPence: number;
  tprmAddonPence: number;
  doraAddonPence: number;
  esgAddonPence: number;
  auditorAccessAddonPence: number;
  jurisdictionPence: number;
  hardeningPence: number;
  bundleFlatPence: number;
  aiCreditPence: number;
  // Enterprise scaling (backend lib/pricing.ts): people in scope from the organisation's
  // headcount, managed endpoints from the assets discovery found — both read server-side.
  headcount: number | null;
  peopleScalingPence: number;
  peopleScalingRule: PeopleScalingRule;
  enterpriseRequired: boolean;
  endpointCount: number;
  endpointScalingPence: number;
  dedicatedQuote: boolean;
  platformPence: number;
  bureauCertPence: number;
  bureauSurveillancePence: number;
  aiSpendPence: number;
  consultancyPence: number;
  totalPence: number;
}

/** What each standard is, for the Profile list. Names and order come from the catalogue, so a standard
 *  added there appears here too (a hard-coded list once left six of a client's standards out). */
const FRAMEWORK_SUBTITLES: Partial<Record<FrameworkId, string>> = {
  iso27001: "Information Security Management System",
  cyber_essentials: "UK Government cybersecurity baseline",
  cyber_essentials_plus: "Independently verified Cyber Essentials",
  iso42001: "Artificial Intelligence Management System",
  iso9001: "Quality Management System",
  soc2: "Security, Availability & Confidentiality (AICPA)",
  pci_dss: "Payment Card Industry Data Security Standard",
  iasme_ca_sole_trader: "IASME Cyber Assurance (1–2 people)",
  iasme_ca_micro: "IASME Cyber Assurance (3–9 people)",
  iasme_ca_small: "IASME Cyber Assurance (10–49 people)",
  iasme_ca_medium_large: "IASME Cyber Assurance (50+ people)",
  nist_csf: "Cybersecurity maturity reference (free alongside a paid standard)",
  gdpr: "EU data protection law",
  uk_gdpr: "UK data protection law",
  iso45001: "Occupational Health and Safety Management System",
  iso14001: "Environmental Management System (ESG add-on)",
  iwa48: "ESG principles maturity (ESG add-on)",
  ncsc_caf: "NCSC Cyber Assessment Framework",
};
const ALL_FRAMEWORKS: Array<{ id: string; name: string; subtitle: string }> = FRAMEWORK_ORDER.map((id) => ({
  id, name: FRAMEWORK_LABELS[id], subtitle: FRAMEWORK_SUBTITLES[id] ?? "",
}));

interface ClauseEntry {
  controlId: string;
  name: string;
  description: string;
  status: string | null;
  notes: string | null;
  aiNote: string | null;
  soaStatus: string | null;
}

interface ClientDoc {
  id: string;
  fileName: string;
  sizeBytes: number;
  uploadedAt: string;
  version: number;
  controls: Array<{ frameworkId: string; controlRef: string }>;
}

type ClauseStatus = "approved" | "needs_work" | "observation";

// ── Billing ───────────────────────────────────────────────────────────────

// Fallback only — mirrors backend DEFAULT_CONSULTANCY_RATES ("consultant" grade).
// The live figures come from GET /consultant/rates (the consultancy's Rate Card page).
const DAY_RATE_PENCE  = 65_000;
const HOUR_RATE_PENCE = 15_000;

interface RateCardEntry {
  label: string;
  dayPence: number;
  hourPence: number;
  monthPence: number;     // managed service monthly fee; 0 = not offered at this grade
  managedDays: number;
  managedHours: number;
}
type RateCard = Record<string, RateCardEntry>;
const RATE_GRADES = ["senior", "consultant", "junior"] as const;

/**
 * The billing preview for the Services tab. The platform fee is the API's own
 * breakdown (the same computation that bills the client), so nothing here
 * re-prices the platform; only what the consultant is about to change is
 * re-derived: the tier add-on for a tier not yet saved (pay-as-you-go only — a
 * package's price already includes its tier) and the consultancy time.
 */
function billingPreview(org: {
  breakdown: BillingBreakdown;
  tierLevel: number;
  consultancyDaysPerMonth: number;
  consultancyHoursRemaining: number;
  dayRatePence: number;
  hourRatePence: number;
}) {
  const bb = org.breakdown;
  const tierAddon = bb.model === "payg" ? (TIER_ADDON_PENCE[org.tierLevel] ?? 0) : bb.tierAddonPence;
  // Full Service includes auditor access, so a preview at L5 drops the add-on line (backend lib/auditorAccess.ts).
  const auditorAccess = org.tierLevel >= 5 ? 0 : (bb.auditorAccessAddonPence ?? 0);
  const platform = bb.platformPence - bb.tierAddonPence + tierAddon - (bb.auditorAccessAddonPence ?? 0) + auditorAccess;
  const standardsTier = bb.bundleFlatPence + bb.standardsFeePence + tierAddon;
  const bureau = bb.bureauCertPence + bb.bureauSurveillancePence;
  const consultancy = consultancyAllocationPence({
    tierLevel: org.tierLevel, days: org.consultancyDaysPerMonth, hours: org.consultancyHoursRemaining,
    dayRatePence: org.dayRatePence, hourRatePence: org.hourRatePence,
  });
  return {
    platform,
    standardsTier,
    integrations: bb.integrationAddonPence,
    tprm: bb.tprmAddonPence,
    dora: bb.doraAddonPence,
    esg: bb.esgAddonPence,
    auditorAccess,
    jur: bb.jurisdictionPence,
    hardening: bb.hardeningPence,
    bureauCert: bb.bureauCertPence,
    bureauAnnual: bb.bureauSurveillancePence,
    bureau,
    consultancy,
    total: platform + bureau + consultancy,
  };
}

/** "Standards & tier", "Small Business Bundle", "CE Annual Package (micro)" — the platform line's label. */
function platformLineLabel(org: { smallBusinessPackage: boolean; bundleTier: string | null; frameworks: Array<{ frameworkId: string }> }): string {
  const pkg = packageOf(org);
  // Only the standards this line pays for: ISO 14001 / IWA 48 are the ESG add-on's, and laws and free
  // reference standards are never on the standards ladder.
  const billed = org.frameworks.filter((f) => !ESG_FRAMEWORK_IDS.has(f.frameworkId as FrameworkId) && !UNPAID_FRAMEWORK_IDS.has(f.frameworkId)).length;
  if (pkg) return `${pkg.name} (${billed} enrolled)`;
  return `Standards & tier (${billed} enrolled)`;
}

// ── Helpers ───────────────────────────────────────────────────────────────

function pence(p: number) {
  return `£${(p / 100).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function clauseStatusColor(status: string | null) {
  if (status === "approved") return theme.color.success;
  if (status === "needs_work") return theme.color.danger;
  if (status === "observation") return theme.color.warning;
  return theme.color.border;
}

function clauseStatusLabel(status: string | null) {
  if (status === "approved") return "Approved";
  if (status === "needs_work") return "Needs Work";
  if (status === "observation") return "Observation";
  return "Pending";
}

function clauseStatusTone(status: string | null): StatusTone {
  if (status === "approved") return "met";
  if (status === "needs_work") return "gap";
  if (status === "observation") return "partial";
  return "none";
}

// ── Sub-components ────────────────────────────────────────────────────────

function AddOnRow({ label, description, enabled, saving, onToggle, style: extraStyle }: {
  label: string; description: string; enabled: boolean;
  saving: boolean; onToggle: () => void; style?: React.CSSProperties;
}) {
  return (
    <div
      style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        background: theme.color.bg, border: `1px solid ${enabled ? theme.color.success + "66" : theme.color.border}`,
        borderRadius: 3, padding: "14px 16px", ...extraStyle,
      }}
    >
      <div>
        <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading }}>{label}</div>
        <div style={{ fontSize: 11, color: theme.color.subtle, marginTop: 2 }}>{description}</div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <span style={{
          fontSize: 11, padding: "3px 10px", borderRadius: 3, fontWeight: 600,
          background: enabled ? theme.color.success + "22" : theme.color.bg,
          color: enabled ? theme.color.successText : theme.color.subtle,
          border: `1px solid ${enabled ? theme.color.success + "55" : theme.color.border}`,
        }}>
          {enabled ? "Active" : "Inactive"}
        </span>
        <button
          onClick={onToggle}
          disabled={saving}
          style={{
            background: enabled ? "none" : theme.color.accent,
            border: enabled ? `1px solid ${theme.color.danger}` : "none",
            borderRadius: 3, padding: "6px 14px", fontSize: 12, cursor: "pointer",
            color: enabled ? theme.color.dangerText : "#fff",
            opacity: saving ? 0.5 : 1, fontWeight: 600,
          }}
        >
          {saving ? "Saving…" : enabled ? "Disable" : "Enable"}
        </button>
      </div>
    </div>
  );
}

function SoaBar({ stats }: { stats: { compliant: number; partial: number; gap: number } }) {
  const total = stats.compliant + stats.partial + stats.gap;
  if (total === 0) return <div style={{ height: 8, background: theme.color.border, borderRadius: 4 }} />;
  return (
    <div style={{ display: "flex", height: 8, borderRadius: 4, overflow: "hidden", gap: 1 }}>
      <div style={{ flex: stats.compliant, background: theme.color.success }} />
      <div style={{ flex: stats.partial, background: theme.color.warning }} />
      <div style={{ flex: stats.gap, background: theme.color.danger }} />
    </div>
  );
}

function KpiTile({ label, value, sub, color }: { label: string; value: string; sub?: string; color?: string }) {
  return (
    <div style={{ ...card, flex: 1, minWidth: 140 }}>
      <div style={{ fontSize: 10, letterSpacing: "0.08em", color: theme.color.faint, textTransform: "uppercase", marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color: color ?? theme.color.heading, lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: theme.color.subtle, marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function Stepper({ value, onChange, step = 1, min = 0 }: {
  value: number; onChange: (v: number) => void; step?: number; min?: number;
}) {
  const btnStyle: React.CSSProperties = {
    width: 32, height: 32, borderRadius: 3, border: `1px solid ${theme.color.border}`,
    background: theme.color.bg, color: theme.color.text, cursor: "pointer", fontSize: 16,
  };
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <button onClick={() => onChange(Math.max(min, value - step))} style={btnStyle}>−</button>
      <span style={{ width: 40, textAlign: "center", fontSize: 15, fontWeight: 700, color: theme.color.heading }}>{value}</span>
      <button onClick={() => onChange(value + step)} style={btnStyle}>+</button>
    </div>
  );
}

// ── ClauseCard ─────────────────────────────────────────────────────────────

function ClauseCard({
  clause,
  orgId,
  onSaved,
}: {
  clause: ClauseEntry;
  orgId: string;
  onSaved: () => void;
}) {
  const [status, setStatus] = useState<string | null>(clause.status);
  const [toggling, setToggling] = useState(false);
  const [toggleError, setToggleError] = useState("");

  const toggleStatus = async (s: ClauseStatus) => {
    const prev = status;
    const next = status === s ? null : s;
    setStatus(next);
    setToggleError("");
    setToggling(true);
    try {
      await api.patch(`/consultant/clients/${orgId}/clauses/${clause.controlId}`, { status: next });
      onSaved();
    } catch {
      setStatus(prev);
      setToggleError("Failed to save — please try again.");
    } finally {
      setToggling(false);
    }
  };

  const borderColor = clauseStatusColor(status);

  return (
    <div
      style={{
        background: theme.color.bg, borderRadius: 3,
        border: `1px solid ${theme.color.border}`,
        borderLeft: `4px solid ${borderColor}`,
        padding: 16, display: "flex", flexDirection: "column", gap: 10,
      }}
    >
      <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
        <span
          style={{
            fontSize: 10, padding: "3px 8px", borderRadius: 4, fontWeight: 700,
            background: theme.color.panel, color: theme.color.accentText,
            whiteSpace: "nowrap", flexShrink: 0,
          }}
        >
          {controlRefWithStandard(clause.controlId) ?? displayControlRef(clause.controlId)}
        </span>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading }}>{displayControlTitle(clause.controlId, clause.name)}</div>
          <div style={{ fontSize: 11, color: theme.color.subtle, marginTop: 2 }}>{clause.description}</div>
        </div>
        {status && <StatusTag tone={clauseStatusTone(status)}>{clauseStatusLabel(status)}</StatusTag>}
      </div>

      {/* Status toggles */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        {(["approved", "needs_work", "observation"] as ClauseStatus[]).map((s) => {
          const active = status === s;
          const colors: Record<ClauseStatus, string> = {
            approved: theme.color.success,
            needs_work: theme.color.danger,
            observation: theme.color.warning,
          };
          const icons: Record<ClauseStatus, string> = {
            approved: "✓ Approved",
            needs_work: "! Needs Work",
            observation: "◎ Observation",
          };
          return (
            <button
              key={s}
              onClick={() => toggleStatus(s)}
              disabled={toggling}
              style={{
                background: active ? `${colors[s]}22` : "transparent",
                border: `1px solid ${active ? colors[s] : theme.color.border}`,
                borderRadius: 3, padding: "5px 12px", fontSize: 12,
                color: active ? colors[s] : theme.color.muted,
                cursor: toggling ? "not-allowed" : "pointer",
                fontWeight: active ? 700 : 400,
                opacity: toggling ? 0.6 : 1,
                transition: "all 0.15s",
              }}
            >
              {icons[s]}
            </button>
          );
        })}
        {toggleError && (
          <span style={{ fontSize: 11, color: theme.color.dangerText }}>{toggleError}</span>
        )}
      </div>

      {/* AI flag */}
      {clause.aiNote && (
        <div style={{ fontSize: 11, color: theme.color.warningText, background: `${theme.color.warning}18`, border: `1px solid ${theme.color.warning}44`, borderRadius: 3, padding: "6px 10px", display: "flex", gap: 6, alignItems: "flex-start" }}>
          <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>{clause.aiNote}</span>
        </div>
      )}

      <span style={{ fontSize: 11, color: status === "approved" ? theme.color.successText : theme.color.subtle }}>
        {status === "approved" ? "✓ Visible to auditor" : "Hidden from auditor"}
      </span>

      {/* Same thread the client reads on their own control pages — notes marked
          visible to the client appear there. */}
      <ControlComments
        controlId={clause.controlId}
        open={true}
        label="Notes"
        orgId={orgId}
        consultantMode={true}
        legacyNote={clause.notes}
        onPost={(body) => {
          api.patch(`/consultant/clients/${orgId}/clauses/${clause.controlId}`, { notes: body })
            .catch((e) => setToggleError(`The note is in the thread but was not saved to the clause record${e instanceof Error && e.message ? `: ${e.message}` : ""}`));
        }}
      />
    </div>
  );
}

// ── OverviewTab ───────────────────────────────────────────────────────────


// Framework colours come from the one source the navigation uses (nav/frameworkMeta.ts).
const FW_COLORS: Record<string, string> = Object.fromEntries(
  ["iso27001", "cyber_essentials", "cyber_essentials_plus", "iso42001", "iso9001", "soc2", "pci_dss",
    "iasme_ca_sole_trader", "iasme_ca_micro", "iasme_ca_small", "iasme_ca_medium_large"].map((id) => [id, frameworkColor(id)]),
);

function OverviewTab({ org, onRefresh }: { org: ClientDetail; onRefresh: () => void }) {
  // Server-computed figures — must match what the client is actually invoiced.
  const bb = org.billingBreakdown;
  const bureauCertMonthly = bb.bureauCertPence;
  const bureauSurveillanceMonthly = bb.bureauSurveillancePence;
  const consultancyPence = bb.consultancyPence;

  // Build KPI tiles mirroring what the client sees on their dashboard
  interface Kpi { label: string; value: string; sub: string; color: string }
  const kpis: Kpi[] = [];

  const enrolledIds = new Set(org.frameworks.map((f) => f.frameworkId));

  if (enrolledIds.has("iso27001")) {
    const fs = org.frameworkStats.find((f) => f.frameworkId === "iso27001");
    // As on the client's Dashboard: the AI check's own figure, labelled as such.
    kpis.push({
      label: "Last AI check score",
      value: org.lastCheckScore !== null ? `${org.lastCheckScore}%` : "—",
      sub: "from the last compliance check",
      color: scoreColor(org.lastCheckScore) ?? FW_COLORS.iso27001,
    });
    kpis.push({
      label: "Mandatory clauses met",
      value: fs ? `${fs.mandatoryCompliant} / ${fs.mandatoryTotal || "—"}` : "—",
      sub: "ISO 27001:2022 Clauses 4–10",
      color: FW_COLORS.iso27001,
    });
    kpis.push({
      label: "Annex A controls met",
      value: fs ? `${fs.annexCompliant} / ${fs.annexTotal || "—"}` : "—",
      sub: "ISO 27002:2022 A.5–A.8",
      color: FW_COLORS.iso27001,
    });
  }

  if (enrolledIds.has("cyber_essentials_plus") || enrolledIds.has("cyber_essentials")) {
    const color = enrolledIds.has("cyber_essentials_plus") ? FW_COLORS.cyber_essentials_plus : FW_COLORS.cyber_essentials;
    kpis.push({
      label: "CE controls compliant",
      value: `${org.ceCompliant} / ${org.ceTotal}`,
      sub: "individual Cyber Essentials controls",
      color,
    });
  }

  if (enrolledIds.has("iso42001")) {
    const fs = org.frameworkStats.find((f) => f.frameworkId === "iso42001");
    kpis.push({
      label: "AIMS clauses met",
      value: fs ? `${fs.mandatoryCompliant} / ${fs.mandatoryTotal || "—"}` : "—",
      sub: "ISO 42001:2023 Clauses 4–10",
      color: FW_COLORS.iso42001,
    });
    kpis.push({
      label: "AIMS controls met",
      value: fs ? `${fs.annexCompliant} / ${fs.annexTotal || "—"}` : "—",
      sub: "ISO 42001 Annex B",
      color: FW_COLORS.iso42001,
    });
  }

  if (enrolledIds.has("iso9001")) {
    const fs = org.frameworkStats.find((f) => f.frameworkId === "iso9001");
    kpis.push({
      label: "QMS clauses met",
      value: fs ? `${fs.compliant} / ${fs.total || "—"}` : "—",
      sub: "ISO 9001:2015",
      color: FW_COLORS.iso9001,
    });
  }

  if (enrolledIds.has("soc2")) {
    const fs = org.frameworkStats.find((f) => f.frameworkId === "soc2");
    kpis.push({
      label: "Trust service criteria",
      value: fs ? `${fs.compliant} / ${fs.total || "—"}` : "—",
      sub: "SOC 2 compliance",
      color: FW_COLORS.soc2,
    });
  }

  if (enrolledIds.has("pci_dss")) {
    const fs = org.frameworkStats.find((f) => f.frameworkId === "pci_dss");
    kpis.push({
      label: "PCI DSS requirements met",
      value: fs ? `${fs.compliant} / ${fs.total || "—"}` : "—",
      sub: "PCI DSS v4.0",
      color: FW_COLORS.pci_dss,
    });
  }

  for (const tier of [
    { id: "iasme_ca_sole_trader",  sub: "IASME CA (Sole Trader)" },
    { id: "iasme_ca_micro",        sub: "IASME CA (Micro)" },
    { id: "iasme_ca_small",        sub: "IASME CA (Small)" },
    { id: "iasme_ca_medium_large", sub: "IASME CA (Medium/Large)" },
  ]) {
    if (enrolledIds.has(tier.id)) {
      const fs = org.frameworkStats.find((f) => f.frameworkId === tier.id);
      kpis.push({
        label: "IASME CA themes met",
        value: fs ? `${fs.compliant} / ${fs.total || "—"}` : "—",
        sub: tier.sub,
        color: FW_COLORS[tier.id],
      });
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      {/* KPI grid — mirrors client dashboard */}
      {kpis.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 14 }}>
          {kpis.map((k) => (
            <div key={k.label} style={{ ...card, padding: "18px 20px", borderTop: `3px solid ${k.color}` }}>
              <div style={{ fontSize: 11, color: theme.color.faint, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                {k.label}
              </div>
              <div style={{ fontSize: 26, fontWeight: 700, color: k.color, lineHeight: 1 }}>{k.value}</div>
              <div style={{ fontSize: 11, color: theme.color.subtle, marginTop: 4 }}>{k.sub}</div>
            </div>
          ))}
        </div>
      )}

      {/* Engagement details */}
      <div style={{ ...card }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: theme.color.heading, marginBottom: 14 }}>Engagement Details</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          {[
            ["CISO", org.ciso ?? "—"],
            ["SOA Score Band", scoreLabel(org.latestScore)],
            ["Last AI Check", fmtDate(org.lastChecked)],
            ["Geography", geographyLabel(org.geography) ?? "—"],
            ["Sector", org.sector ?? "—"],
            ["Staff Size", org.size ?? "—"],
            ["Billing Model", org.edition === "jersey" ? "Jersey package (Abacas Jersey edition)" : packageOf(org)?.name ?? "Pay-as-you-go"],
          ].map(([label, value]) => (
            <div key={label}>
              <div style={{ fontSize: 10, color: theme.color.faint, textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</div>
              <div style={{ fontSize: 13, color: theme.color.text, marginTop: 2 }}>{value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Standards & Frameworks */}
      <div style={{ ...card }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: theme.color.heading, marginBottom: 14 }}>
          Standards &amp; Frameworks
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {ALL_FRAMEWORKS.map((fw) => {
            const enrolled = org.frameworks.find((f) => f.frameworkId === fw.id);
            return (
              <div
                key={fw.id}
                style={{
                  display: "flex", alignItems: "center", gap: 12,
                  padding: "10px 14px", borderRadius: 3,
                  background: enrolled ? theme.color.accentBg : theme.color.panel,
                  border: `1px solid ${enrolled ? theme.color.accent + "55" : theme.color.border}`,
                }}
              >
                <div
                  style={{
                    width: 20, height: 20, borderRadius: "50%", flexShrink: 0,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    background: enrolled ? theme.color.accent : theme.color.border,
                    fontSize: 11, fontWeight: 800, color: enrolled ? "#fff" : theme.color.faint,
                  }}
                >
                  {enrolled ? "✓" : ""}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: enrolled ? theme.color.heading : theme.color.subtle }}>
                    {fw.name}
                  </div>
                  <div style={{ fontSize: 11, color: theme.color.faint }}>{fw.subtitle}</div>
                </div>
                {enrolled ? (
                  <span style={{ fontSize: 11, color: theme.color.accentText, fontWeight: 600 }}>
                    Selected
                  </span>
                ) : (
                  <span style={{ fontSize: 11, color: theme.color.faint }}>Not selected</span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Invoice & Profit */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        {/* Invoice card */}
        <div style={{ ...card }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: theme.color.heading, marginBottom: 14 }}>Monthly Invoice</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {[
              // Platform line: the package flat rate (plus anything it does not cover) or standards + tier on PAYG
              [platformLineLabel(org), pence(bb.bundleFlatPence + bb.standardsFeePence + bb.tierAddonPence)],
              // Everything billed on top, whichever model — 0 lines are hidden
              ...(bb.integrationAddonPence > 0 ? [["Integrations", pence(bb.integrationAddonPence)]] : []),
              ...(bb.tprmAddonPence > 0 ? [["Supplier Risk (TPRM)", pence(bb.tprmAddonPence)]] : []),
              ...(bb.doraAddonPence > 0 ? [["DORA Monitoring", pence(bb.doraAddonPence)]] : []),
              ...(bb.esgAddonPence > 0 ? [["ESG", pence(bb.esgAddonPence)]] : []),
              ...(bb.auditorAccessAddonPence > 0 ? [["Auditor access", pence(bb.auditorAccessAddonPence)]] : []),
              ...(bb.jurisdictionPence > 0 ? [["Jurisdictions", pence(bb.jurisdictionPence)]] : []),
              ...(bb.hardeningPence > 0 ? [["CIS benchmarks", pence(bb.hardeningPence)]] : []),
              // Enterprise scaling — shown only when a line bills
              ...(bb.peopleScalingPence > 0 ? [[peopleScalingLabel(bb.peopleScalingRule, bb.headcount), pence(bb.peopleScalingPence)]] : []),
              ...(bb.endpointScalingPence > 0 ? [[endpointScalingLabel(bb.endpointCount), pence(bb.endpointScalingPence)]] : []),
              ...(bb.dedicatedQuote ? [["Held at the platform ceiling", pence(SCALING_CEILING_PENCE)]] : []),
              ["Cert fee (bureau)", pence(bureauCertMonthly)],
              ["Surveillance (bureau)", pence(bureauSurveillanceMonthly)],
              ["AI usage", pence(org.monthlyAiSpendPence)],
              ["Consultancy (allocation)", pence(consultancyPence)],
            ].map(([label, value]) => (
              <div key={label} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: theme.color.muted }}>
                <span>{label}</span><span>{value}</span>
              </div>
            ))}
            <div
              style={{
                display: "flex", justifyContent: "space-between", fontSize: 13,
                fontWeight: 700, color: theme.color.heading,
                borderTop: `1px solid ${theme.color.border}`, paddingTop: 8, marginTop: 4,
              }}
            >
              <span>Total</span><span>{pence(org.monthlyBilledPence)}</span>
            </div>
          </div>
        </div>

        {/* Revenue split card */}
        <div style={{ ...card }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: theme.color.heading, marginBottom: 4 }}>Revenue Split</div>
          <div style={{ fontSize: 11, color: theme.color.faint, marginBottom: 14 }}>Consultancy is billed at the allocation; bookings and logged time are what was delivered</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {[
              { label: "Platform & AI (Abacas)", value: org.monthlyAbacasRevenuePence, color: theme.color.muted },
              { label: "Your time (allocation, billed)", value: org.monthlyConsultancyRevenuePence, color: theme.color.successText },
              { label: "Delivered this month (bookings and logged time)", value: org.monthlyConsultancyDeliveredPence, color: theme.color.muted },
            ].map((r) => (
              <div key={r.label} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: theme.color.muted }}>
                <span>{r.label}</span>
                <span style={{ color: r.color, fontWeight: 600 }}>{pence(r.value)}</span>
              </div>
            ))}
            <div
              style={{
                display: "flex", justifyContent: "space-between",
                fontSize: 13, fontWeight: 700, color: theme.color.heading,
                borderTop: `1px solid ${theme.color.border}`, paddingTop: 8, marginTop: 4,
              }}
            >
              <span>Total billed</span>
              <span>{pence(org.monthlyBilledPence)}</span>
            </div>
            {deliveryDifferenceNote(org.consultancyDeliveryFlag, org.consultancyDeliveryDifferencePence, pence) && (
              <div role="note" style={{ fontSize: 11, color: theme.color.warning }}>
                {deliveryDifferenceNote(org.consultancyDeliveryFlag, org.consultancyDeliveryDifferencePence, pence)}
              </div>
            )}
          </div>
        </div>
      </div>

    </div>
  );
}

// ── ServicesTab ────────────────────────────────────────────────────────────

/**
 * People in scope as agreed in the contract at purchase: what the fee is billed on. The directory's
 * enabled accounts raise it only when they are higher, and the extra people are shown here, never
 * silently added.
 */
function ContractedHeadcountField({ org, onSaved }: { org: ClientDetail; onSaved: () => void }) {
  const [value, setValue] = useState(org.contractedHeadcount == null ? "" : String(org.contractedHeadcount));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const hb = org.headcountBasis;
  const unchanged = value.trim() === (org.contractedHeadcount == null ? "" : String(org.contractedHeadcount));

  const save = async () => {
    const n = value.trim() === "" ? null : Number(value);
    if (n !== null && (!Number.isInteger(n) || n <= 0)) { setError("Enter a whole number of people, or leave it empty."); return; }
    setSaving(true);
    setError("");
    try {
      await api.patch(`/consultant/clients/${org.id}`, { contractedHeadcount: n });
      onSaved();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to save the headcount");
    } finally {
      setSaving(false);
    }
  };

  return (
    <span style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
        Contracted headcount
        <input
          type="number"
          min={1}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="not pinned"
          style={{ ...inputStyle, width: 110, padding: "4px 8px", fontSize: 12 }}
        />
        <button type="button" onClick={save} disabled={saving || unchanged} style={{ ...btnSecondary, padding: "4px 10px", fontSize: 12 }}>
          {saving ? "Saving..." : "Save"}
        </button>
      </label>
      <span style={{ fontSize: 11, color: theme.color.subtle }}>
        {hb.basis === "directory"
          ? `Billed on ${hb.billed?.toLocaleString("en-GB")}: the directory shows ${hb.directory?.toLocaleString("en-GB")} enabled accounts, ${hb.extra.toLocaleString("en-GB")} above the contract.`
          : hb.basis === "size"
            ? "Not pinned: billed on the size the client declared. Enter the figure from the contract."
            : hb.basis === "none"
              ? "Nothing agreed: list price."
              : hb.directory != null
                ? `The directory shows ${hb.directory.toLocaleString("en-GB")} enabled accounts, within the contract.`
                : "No directory connected."}
      </span>
      {error && <span style={{ fontSize: 11, color: theme.color.dangerText }}>{error}</span>}
    </span>
  );
}

function ServicesTab({ org, onRefresh }: { org: ClientDetail; onRefresh: () => void }) {
  // Server-computed billing lines (the same breakdown the client is invoiced on).
  const bb = org.billingBreakdown;
  const [daysPerMonth, setDaysPerMonth] = useState(org.consultancyDaysPerMonth);
  const [hoursRemaining, setHoursRemaining] = useState(org.consultancyHoursRemaining);
  const [previewTierId, setPreviewTierId] = useState(org.serviceTier.id);
  // Bureau chosen per standard but not yet assigned.
  const [pendingBureauByFw, setPendingBureauByFw] = useState<Record<string, string>>({});
  const [savingConsultancy, setSavingConsultancy] = useState(false);
  const [consultancyError, setConsultancyError] = useState("");
  const [savingTier, setSavingTier] = useState(false);
  const [savingBureau, setSavingBureau] = useState(false);
  const [tierError, setTierError] = useState("");
  const [bureauError, setBureauError] = useState("");
  const [savingTprm, setSavingTprm] = useState(false);
  const [tprmError, setTprmError] = useState("");
  const [savingDora, setSavingDora] = useState(false);
  const [doraError, setDoraError] = useState("");
  const [savingEsg, setSavingEsg] = useState(false);
  const [esgError, setEsgError] = useState("");
  const [savingAuditor, setSavingAuditor] = useState(false);
  const [auditorError, setAuditorError] = useState("");
  const [savingLegal, setSavingLegal] = useState(false);
  const [legalError, setLegalError] = useState("");

  // Consultancy rate card (set on the Rate Card page) — drives T&M pricing here
  // and pre-fills the managed service contract from the consultancy-wide offer.
  const [rateCard, setRateCard] = useState<RateCard | null>(null);
  const [rateCardError, setRateCardError] = useState(false);
  useEffect(() => {
    api.get<{ rateCard: RateCard }>("/consultant/rates").then((r) => setRateCard(r.rateCard)).catch(() => setRateCardError(true));
  }, []);
  const tmDayPence  = rateCard?.consultant?.dayPence  ?? DAY_RATE_PENCE;
  const tmHourPence = rateCard?.consultant?.hourPence ?? HOUR_RATE_PENCE;

  // Managed service contract
  const [msDays, setMsDays]             = useState(org.managedServiceDaysPerMonth);
  const [msHours, setMsHours]           = useState(org.managedServiceHoursPerMonth);
  const [msGrade, setMsGrade]           = useState<string | null>(org.managedServiceGrade);
  const [savingMs, setSavingMs]         = useState(false);
  const [msError, setMsError]           = useState("");
  const [clearingMs, setClearingMs]     = useState(false);
  const [msFlatFeeInput, setMsFlatFeeInput] = useState(
    org.managedServiceRatePence != null ? String(Math.round(org.managedServiceRatePence / 100)) : ""
  );
  const applyRateCardOffer = (grade: string) => {
    const e = rateCard?.[grade];
    if (!e || e.monthPence <= 0) return;
    setMsGrade(grade);
    setMsFlatFeeInput(String(Math.round(e.monthPence / 100)));
    setMsDays(e.managedDays);
    setMsHours(e.managedHours);
  };

  const previewTierLevel = org.allTiers.find((t) => t.id === previewTierId)?.level ?? org.serviceTier.level;
  const liveBill = billingPreview({
    breakdown: org.billingBreakdown,
    tierLevel: previewTierLevel,
    consultancyDaysPerMonth: daysPerMonth,
    consultancyHoursRemaining: hoursRemaining,
    dayRatePence: tmDayPence,
    hourRatePence: tmHourPence,
  });
  const savedBill = billingPreview({
    breakdown: org.billingBreakdown,
    tierLevel: org.serviceTier.level,
    consultancyDaysPerMonth: org.consultancyDaysPerMonth,
    consultancyHoursRemaining: org.consultancyHoursRemaining,
    dayRatePence: tmDayPence,
    hourRatePence: tmHourPence,
  });
  const billingChanged = liveBill.total !== savedBill.total;

  const changeTier = async (tierId: string) => {
    if (tierId === org.serviceTier.id) return;
    // Below Consultant-Led there is no consultancy time: the move ends the allocation, so ask first (REP-42).
    const targetLevel = org.allTiers.find((t) => t.id === tierId)?.level ?? org.serviceTier.level;
    const prompt = allocationClearPrompt(org, targetLevel);
    if (prompt && !window.confirm(prompt)) return;
    setPreviewTierId(tierId);
    setSavingTier(true);
    setTierError("");
    try {
      await api.patch(`/consultant/clients/${org.id}`, { serviceTierId: tierId, ...(prompt ? { confirmClearAllocation: true } : {}) });
      onRefresh();
    } catch (e: unknown) {
      setTierError(e instanceof Error ? e.message : "Failed to change tier");
      setPreviewTierId(org.serviceTier.id);
    } finally {
      setSavingTier(false);
    }
  };

  // Billing model: pay-as-you-go or one of the packages. Setting a package
  // puts the client on the tier and add-ons it includes; clearing it leaves
  // them in place, billed at standard rates from then on.
  const [savingPackage, setSavingPackage] = useState(false);
  const [packageError, setPackageError] = useState("");
  // The Jersey package is billed monthly or annually in advance — PATCH /organizations/jersey-billing,
  // which any consultant of the client's own firm may call (as may the client's org:admin).
  const changeJerseyBilling = async (interval: "monthly" | "annual") => {
    setSavingPackage(true);
    setPackageError("");
    try {
      await api.patch("/organizations/jersey-billing", { interval }, { organizationId: org.id });
      onRefresh();
    } catch (e: unknown) {
      setPackageError(e instanceof Error ? e.message : "Failed to change how the package is billed");
    } finally {
      setSavingPackage(false);
    }
  };
  const currentPackage = packageOf(org);
  // Sending the package the client is already on re-applies what it includes.
  const applyPackage = async (id: string | null) => {
    setSavingPackage(true);
    setPackageError("");
    try {
      await api.patch(`/consultant/clients/${org.id}`, { package: id });
      onRefresh();
    } catch (e: unknown) {
      setPackageError(e instanceof Error ? e.message : "Failed to change the billing model");
    } finally {
      setSavingPackage(false);
    }
  };
  const changePackage = (id: string) => {
    if ((id || null) === (currentPackage?.id ?? null)) return;
    void applyPackage(id || null);
  };
  // What the package includes that the client does not yet hold — an organisation
  // put on a package before entitlements were granted automatically, or moved below
  // them since. Empty when everything is in place.
  const packageShortfall: string[] = (() => {
    const pkg = org.package;
    if (!pkg) return [];
    const missing: string[] = [];
    if (pkg.tierFixed ? org.serviceTier.level !== pkg.tierLevel : org.serviceTier.level < pkg.tierLevel) missing.push(`tier ${pkg.tierLevel}`);
    if (pkg.addons.tprm && !org.tprmEnabled) missing.push("TPRM");
    if (pkg.addons.dora && org.doraApplies && !org.doraMonitoringEnabled) missing.push("DORA");
    if (pkg.addons.esg && !org.esgEnabled) missing.push("ESG");
    return missing;
  })();
  // Why a tier card is greyed out, or null when the client may be moved onto it.
  const tierBlockedReason = (level: number): string | null => {
    const pkg = org.package;
    if (!pkg) return null;
    if (pkg.tierFixed && level !== pkg.tierLevel) return `Fixed by the ${pkg.name}`;
    if (level < pkg.tierLevel) return `Below the tier the ${pkg.name} includes`;
    return null;
  };

  // Assign (or, with null, end) the bureau for one standard. A consultant's
  // assignment overrides an existing lock; the client's own picker cannot.
  const assignBureau = async (frameworkId: string, bureauId: string | null) => {
    setSavingBureau(true);
    setBureauError("");
    try {
      await api.put(`/consultant/clients/${org.id}/engagements/${frameworkId}`, { bureauId });
      setPendingBureauByFw((prev) => { const next = { ...prev }; delete next[frameworkId]; return next; });
      onRefresh();
    } catch (e: unknown) {
      setBureauError(e instanceof Error ? e.message : "Failed to update the bureau for this standard");
    } finally {
      setSavingBureau(false);
    }
  };

  const releaseLock = async (frameworkId: string) => {
    setSavingBureau(true);
    setBureauError("");
    try {
      await api.post(`/consultant/clients/${org.id}/engagements/${frameworkId}/unlock`, {});
      onRefresh();
    } catch {
      setBureauError("Failed to release lock");
    } finally {
      setSavingBureau(false);
    }
  };

  const approveOrDeclineTier = async (action: "approved" | "declined") => {
    if (!org.pendingTierRequest) return;
    // Approving a move below Consultant-Led ends the client's consultancy allocation: ask first (REP-42).
    const requestedLevel = org.allTiers.find((t) => t.id === org.pendingTierRequest!.requestedTierId)?.level ?? org.serviceTier.level;
    const prompt = action === "approved" ? allocationClearPrompt(org, requestedLevel) : null;
    if (prompt && !window.confirm(prompt)) return;
    try {
      await api.patch(`/consultant/tier-requests/${org.pendingTierRequest.id}`, { action, ...(prompt ? { confirmClearAllocation: true } : {}) });
      setTierError("");
    } catch (e: unknown) {
      setTierError(e instanceof Error ? e.message : "Failed to resolve the request");
      return;
    }
    onRefresh();
  };

  const saveConsultancy = async () => {
    setSavingConsultancy(true); setConsultancyError("");
    try {
      await api.patch(`/consultant/clients/${org.id}`, {
        consultancyDaysPerMonth: daysPerMonth,
        consultancyHoursRemaining: hoursRemaining,
      });
      onRefresh();
    } catch {
      setConsultancyError("Failed to save. Please try again.");
    } finally {
      setSavingConsultancy(false);
    }
  };

  const toggleTprm = async () => {
    setSavingTprm(true);
    setTprmError("");
    try {
      await api.patch(`/consultant/clients/${org.id}`, { tprmEnabled: !org.tprmEnabled });
      onRefresh();
    } catch {
      setTprmError("Failed to update TPRM addon. Please try again.");
    } finally {
      setSavingTprm(false);
    }
  };

  const toggleDora = async () => {
    setSavingDora(true);
    setDoraError("");
    try {
      await api.patch(`/consultant/clients/${org.id}`, { doraMonitoringEnabled: !org.doraMonitoringEnabled });
      onRefresh();
    } catch {
      setDoraError("Failed to update DORA Monitoring addon. Please try again.");
    } finally {
      setSavingDora(false);
    }
  };

  const toggleEsg = async () => {
    setSavingEsg(true);
    setEsgError("");
    try {
      await api.patch(`/consultant/clients/${org.id}`, { esgEnabled: !org.esgEnabled });
      onRefresh();
    } catch {
      setEsgError("Failed to update ESG addon. Please try again.");
    } finally {
      setSavingEsg(false);
    }
  };

  const toggleAuditorAccess = async () => {
    setSavingAuditor(true);
    setAuditorError("");
    try {
      await api.patch(`/consultant/clients/${org.id}`, { auditorAccessEnabled: !org.auditorAccessEnabled });
      onRefresh();
    } catch {
      setAuditorError("Failed to update the auditor access add-on. Please try again.");
    } finally {
      setSavingAuditor(false);
    }
  };

  const toggleLegal = async () => {
    setSavingLegal(true);
    setLegalError("");
    try {
      await api.patch(`/consultant/clients/${org.id}`, { legalObligationsEnabled: !org.legalObligationsEnabled });
      onRefresh();
    } catch {
      setLegalError("Failed to update Legal Obligations addon. Please try again.");
    } finally {
      setSavingLegal(false);
    }
  };

  const msFlatFeePence = Math.round(parseFloat(msFlatFeeInput || "0") * 100);

  const saveManagedService = async () => {
    if (!msFlatFeePence || msFlatFeePence <= 0) {
      setMsError("Enter a monthly flat fee greater than zero.");
      return;
    }
    setSavingMs(true); setMsError("");
    try {
      await api.patch(`/consultant/clients/${org.id}`, {
        managedServiceRatePence: msFlatFeePence,
        managedServiceGrade: msGrade,
        managedServiceDaysPerMonth: msDays,
        managedServiceHoursPerMonth: msHours,
      });
      onRefresh();
    } catch {
      setMsError("Failed to save managed service contract. Please try again.");
    } finally {
      setSavingMs(false);
    }
  };

  const clearManagedService = async () => {
    setClearingMs(true); setMsError("");
    try {
      await api.patch(`/consultant/clients/${org.id}`, { managedServiceRatePence: null });
      setMsDays(0); setMsHours(0); setMsFlatFeeInput(""); setMsGrade(null);
      onRefresh();
    } catch {
      setMsError("Failed to remove managed service contract.");
    } finally {
      setClearingMs(false);
    }
  };

  // Standards a bureau can be engaged for: those at least one bureau is accredited to audit.
  const certifiable = org.frameworks
    .map((f) => f.frameworkId)
    .filter((id) => org.allBureaux.some((b) => b.frameworkIds.includes(id)) || org.engagements.some((e) => e.frameworkId === id));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      {/* Billing model */}
      <div style={{ ...card }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: theme.color.heading, marginBottom: 4 }}>Billing Model</div>
        <div style={{ fontSize: 12, color: theme.color.subtle, marginBottom: 14 }}>
          Pay-as-you-go bills every standard, the tier and each add-on separately. A package is a flat rate that
          includes a service tier and add-ons — choosing one applies them to the client at once.
        </div>
        {packageError && <div style={{ color: theme.color.dangerText, fontSize: 12, marginBottom: 8 }}>{packageError}</div>}
        {org.edition === "jersey" ? (
          // The Jersey edition carries its own package: it cannot be changed from here (the API refuses it).
          <div style={{ fontSize: 12, color: theme.color.text, lineHeight: 1.6, padding: "10px 14px", background: theme.color.accentBg, borderRadius: 3 }}>
            <strong style={{ color: theme.color.accentText }}>Abacas Jersey edition</strong> — the client signs in at jsy.abacassecurity.com.
            {org.jerseyPackage && (
              <>
                {" "}Jersey package: {gbp(org.jerseyPackage.annualPence)} a year, billed
                {org.jerseyPackage.billingInterval === "annual" ? " annually in advance" : ` monthly (${gbpMo(org.jerseyPackage.monthlyPence)})`};
                consultancy {org.jerseyPackage.consultancyHoursByYear.join(" / ")} hours by year; the certificate
                ({gbp(org.jerseyPackage.certificateAnnualPence)} a year) and surveillance included
                {org.jerseyPackage.termYear ? ` — year ${org.jerseyPackage.termYear} of 3` : ""}
                {org.jerseyPackage.termEndsAt ? `, term ends ${new Date(org.jerseyPackage.termEndsAt).toLocaleDateString("en-GB")}` : ""}.
              </>
            )}
            {org.billingBreakdown?.model === "jersey_licensed" && " Covered by your consultancy's Jersey seat licence."}
            {org.jerseyPackage && (
              <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, fontSize: 12, color: theme.color.subtle }}>
                Billed
                <select
                  value={org.jerseyPackage.billingInterval === "annual" ? "annual" : "monthly"}
                  disabled={savingPackage}
                  onChange={(e) => changeJerseyBilling(e.target.value as "monthly" | "annual")}
                  style={{ ...inputStyle, width: 200, padding: "4px 8px", fontSize: 12 }}
                >
                  <option value="monthly">Monthly</option>
                  <option value="annual">Annually in advance</option>
                </select>
              </label>
            )}
          </div>
        ) : (
        <select
          value={currentPackage?.id ?? ""}
          disabled={savingPackage}
          onChange={(e) => changePackage(e.target.value)}
          style={{ ...inputStyle, cursor: savingPackage ? "wait" : "pointer", maxWidth: 520 }}
        >
          <option value="">Pay-as-you-go — standard rates</option>
          {PACKAGES.map((p) => (
            <option key={p.id} value={p.id}>{p.option}</option>
          ))}
        </select>
        )}
        {currentPackage && org.edition !== "jersey" && (
          <div style={{ fontSize: 12, color: theme.color.subtle, marginTop: 8, lineHeight: 1.5 }}>
            {currentPackage.summary}
          </div>
        )}
        {org.package && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 }}>
            {[
              `Tier ${org.package.tierLevel}${org.package.tierFixed ? " (fixed)" : " minimum"}`,
              ...(org.package.addons.tprm ? ["TPRM included"] : []),
              ...(org.package.addons.dora ? ["DORA included"] : []),
              ...(org.package.addons.esg ? ["ESG included"] : []),
              org.package.jurisdictionAllowance === null ? "Unlimited jurisdictions" : `${org.package.jurisdictionAllowance} jurisdiction${org.package.jurisdictionAllowance === 1 ? "" : "s"} included`,
              `${gbpMo(org.package.aiCreditPence)} AI credit`,
            ].map((chip) => (
              <span key={chip} style={{ fontSize: 11, padding: "2px 8px", borderRadius: 4, background: theme.color.accentBg, color: theme.color.accentText, fontWeight: 600 }}>
                {chip}
              </span>
            ))}
          </div>
        )}
        {org.package && packageShortfall.length > 0 && (
          <div style={{ marginTop: 12, padding: "10px 14px", background: theme.color.warningBg, border: `1px solid ${theme.color.warning}`, borderRadius: 3, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <div style={{ fontSize: 12, color: theme.color.warningText, flex: 1, minWidth: 220, lineHeight: 1.5 }}>
              The client is billed for the {org.package.name} but does not yet hold {packageShortfall.join(", ")} it includes.
            </div>
            <button
              onClick={() => applyPackage(org.package!.id)}
              disabled={savingPackage}
              style={{ ...btnPrimary, padding: "6px 14px", fontSize: 12, opacity: savingPackage ? 0.6 : 1 }}
            >
              {savingPackage ? "Applying…" : "Apply what the package includes"}
            </button>
          </div>
        )}

        {/* Enterprise scaling — people in scope and managed endpoints, read from the client's own rows */}
        <div style={{ marginTop: 14, padding: "10px 14px", background: theme.color.panelAlt, border: `1px solid ${theme.color.border}`, borderRadius: 3, fontSize: 12, color: theme.color.text, lineHeight: 1.6 }}>
          <div style={{ fontSize: 11, color: theme.color.faint, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>Enterprise scaling</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 24px" }}>
            <span>
              People in scope: <strong>{bb.headcount === null ? "not set" : bb.headcount.toLocaleString("en-GB")}</strong>
              {bb.peopleScalingPence > 0 ? ` — ${pence(bb.peopleScalingPence)}/mo` : bb.headcount === null ? " (list price; set the headcount in the client's organisation profile)" : " (list price)"}
            </span>
            <ContractedHeadcountField org={org} onSaved={onRefresh} />
            <span>
              Managed endpoints: <strong>{bb.endpointCount.toLocaleString("en-GB")}</strong>
              {bb.endpointScalingPence > 0 ? ` — ${pence(bb.endpointScalingPence)}/mo above the ${ENDPOINTS_INCLUDED} included` : ` (${ENDPOINTS_INCLUDED} included)`}
            </span>
          </div>
          <div style={{ fontSize: 11, color: theme.color.faint, marginTop: 4 }}>
            Endpoints are the assets discovery found through Intune, Defender, AWS, GCP or Sentinel and has seen in the last 90 days; hand-entered assets are never billed. Logins are unlimited.
            {(bb.headcount ?? 0) >= SCALING_CONTRACT_NOTES.commitmentPeople && ` Contract terms at ${SCALING_CONTRACT_NOTES.commitmentPeople.toLocaleString("en-GB")}+ people: ${SCALING_CONTRACT_NOTES.commitmentMonths}-month commitment, ${SCALING_CONTRACT_NOTES.prepayDiscountPercent}% off for annual prepayment.`}
          </div>
        </div>
        {bb.enterpriseRequired && (
          <div style={{ marginTop: 10, padding: "10px 14px", background: theme.color.warningBg, border: `1px solid ${theme.color.warning}`, borderRadius: 3, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <div style={{ fontSize: 12, color: theme.color.warningText, flex: 1, minWidth: 220, lineHeight: 1.5 }}>
              {org.package ? `The ${org.package.name}` : "Pay-as-you-go"} is not offered above {PAYG_PEOPLE_MAX} people in scope — the published price for this client is the Enterprise bundle. The fee shown is the current model's until you move them.
            </div>
            <button
              onClick={() => applyPackage("enterprise")}
              disabled={savingPackage}
              style={{ ...btnPrimary, padding: "6px 14px", fontSize: 12, opacity: savingPackage ? 0.6 : 1 }}
            >
              {savingPackage ? "Applying…" : "Move to the Enterprise bundle"}
            </button>
          </div>
        )}
        {bb.dedicatedQuote && (
          <div style={{ marginTop: 10, padding: "10px 14px", background: theme.color.warningBg, border: `1px solid ${theme.color.warning}`, borderRadius: 3, fontSize: 12, color: theme.color.warningText, lineHeight: 1.5 }}>
            Dedicated-instance quote: the people and endpoint components exceed the {gbpMo(SCALING_CEILING_PENCE)} platform ceiling, so the fee is held there. Contact sales@abacassecurity.com for a dedicated-instance proposal.
          </div>
        )}
      </div>

      {/* Service tier */}
      <div style={{ ...card }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: theme.color.heading, marginBottom: 4 }}>Service Tier</div>
        <div style={{ fontSize: 12, color: theme.color.subtle, marginBottom: 14 }}>
          {org.package
            ? org.package.tierFixed
              ? `The ${org.package.name} includes the Consultant-Led tier at the package rate. Clear the package above to change the tier.`
              : `The ${org.package.name} includes tier ${org.package.tierLevel}; the client can be moved up but not below it.`
            : "Click a tier card to change the client's plan."}
        </div>
        {tierError && <div style={{ color: theme.color.dangerText, fontSize: 12, marginBottom: 8 }}>{tierError}</div>}

        {/* Pending tier request */}
        {org.pendingTierRequest && (
          <div
            style={{
              background: theme.color.warningBg, border: `1px solid ${theme.color.warning}`,
              borderRadius: 3, padding: 12, marginBottom: 14, fontSize: 13,
            }}
          >
            <div style={{ color: theme.color.warningText, fontWeight: 600, marginBottom: 6 }}>
              Pending upgrade request → {org.pendingTierRequest.requestedTier.name}
            </div>
            {org.pendingTierRequest.note && (
              <div style={{ color: theme.color.muted, fontSize: 12, marginBottom: 8 }}>"{org.pendingTierRequest.note}"</div>
            )}
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={() => approveOrDeclineTier("approved")}
                style={{ ...btnPrimary, background: theme.color.success, padding: "6px 14px", fontSize: 12 }}
              >
                Approve
              </button>
              <button
                onClick={() => approveOrDeclineTier("declined")}
                style={{
                  background: "none", border: `1px solid ${theme.color.danger}`,
                  borderRadius: 3, padding: "6px 14px", fontSize: 12,
                  color: theme.color.dangerText, cursor: "pointer",
                }}
              >
                Decline
              </button>
            </div>
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 10 }}>
          {org.allTiers.map((t) => {
            const active = t.id === org.serviceTier.id;
            const blocked = active ? null : tierBlockedReason(t.level);
            return (
              <button
                key={t.id}
                onClick={() => changeTier(t.id)}
                disabled={savingTier || active || !!blocked}
                title={blocked ?? undefined}
                style={{
                  background: active ? theme.color.accentBg : theme.color.bg,
                  border: `2px solid ${active ? theme.color.accent : theme.color.border}`,
                  borderRadius: 3, padding: 14, cursor: active || blocked ? "default" : "pointer",
                  textAlign: "left", opacity: (savingTier && !active) || blocked ? 0.45 : 1,
                  transition: "border-color 0.15s",
                }}
              >
                <div style={{ fontSize: 12, fontWeight: 700, color: active ? theme.color.accentText : theme.color.heading }}>
                  {t.name}
                </div>
                <div style={{ fontSize: 11, color: theme.color.muted, marginTop: 4 }}>
                  Level {t.level} — {pence(t.basePlatformFeePence)}/mo platform fee
                </div>
                {t.level >= 4 && (
                  <div style={{ fontSize: 10, color: theme.color.warningText, marginTop: 4, lineHeight: 1.4 }}>
                    + consultant time billed on top (managed service or your day/hour rate){t.level >= 5 ? " + bureau fees at cost" : ""}
                  </div>
                )}
                {active && (
                  <div style={{ fontSize: 10, color: theme.color.accentText, marginTop: 4, fontWeight: 600 }}>
                    ✓ Active
                  </div>
                )}
                {blocked && (
                  <div style={{ fontSize: 10, color: theme.color.faint, marginTop: 4 }}>{blocked}</div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Licensed copies of the client's standards — attested by the client, verified here */}
      <StandardLicencesPanel organizationId={org.id} />

      {/* Certification bureaux — one per standard */}
      <div style={{ ...card }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: theme.color.heading, marginBottom: 4 }}>Certification Bureaux</div>
        <div style={{ fontSize: 12, color: theme.color.subtle, marginBottom: 14 }}>
          One bureau per standard. Assigning a bureau locks that standard with it for 3 years; the client cannot move a
          locked standard, but you can release the lock, reassign it, or end the engagement here.
        </div>
        {bureauError && <div style={{ color: theme.color.dangerText, fontSize: 12, marginBottom: 8 }}>{bureauError}</div>}

        {certifiable.length === 0 ? (
          <div style={{ color: theme.color.subtle, fontSize: 12 }}>
            {org.allBureaux.length === 0 ? "No bureaux registered." : "The client is not enrolled in any standard a registered bureau can certify."}
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {certifiable.map((fw) => {
              const engagement = org.engagements.find((e) => e.frameworkId === fw) ?? null;
              const eligible = org.allBureaux.filter((b) => b.frameworkIds.includes(fw));
              const pending = pendingBureauByFw[fw] ?? "";
              const pendingBureau = eligible.find((b) => b.id === pending) ?? null;
              const pendingPrice = pendingBureau?.prices.find((p) => p.frameworkId === fw) ?? null;
              return (
                <div
                  key={fw}
                  style={{
                    background: theme.color.bg, borderRadius: 3, padding: 14,
                    border: `1px solid ${engagement?.locked ? theme.color.warning : theme.color.border}`,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                    <div style={{ minWidth: 150 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading }}>{FRAMEWORK_LABELS[fw as FrameworkId] ?? fw}</div>
                      {engagement ? (
                        <>
                          <div style={{ fontSize: 12, color: theme.color.text, marginTop: 2 }}>{engagement.bureau.name}</div>
                          {(engagement.bureau.accreditation || engagement.bureau.country) && (
                            <div style={{ fontSize: 11, color: theme.color.muted }}>{[engagement.bureau.accreditation, engagement.bureau.country].filter(Boolean).join(" · ")}</div>
                          )}
                          <div style={{ fontSize: 11, color: engagement.locked ? theme.color.warningText : theme.color.muted, marginTop: 4 }}>
                            {engagement.locked ? `Locked until ${fmtDate(engagement.lockedUntil)}` : "Lock released — client may change bureau"}
                          </div>
                        </>
                      ) : (
                        <div style={{ fontSize: 11, color: theme.color.faint, marginTop: 2, fontStyle: "italic" }}>No bureau engaged</div>
                      )}
                    </div>
                    <div style={{ flex: 1 }} />
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <select
                        value={pending}
                        onChange={(e) => setPendingBureauByFw((prev) => ({ ...prev, [fw]: e.target.value }))}
                        disabled={savingBureau || eligible.length === 0}
                        style={{ ...inputStyle, width: "auto", minWidth: 200, padding: "6px 10px", fontSize: 12 }}
                      >
                        <option value="">{eligible.length === 0 ? "No accredited bureau" : engagement ? "Reassign to…" : "Assign bureau…"}</option>
                        {eligible.filter((b) => b.id !== engagement?.bureau.id).map((b) => {
                          const price = b.prices.find((p) => p.frameworkId === fw);
                          const monthly = price ? Math.ceil(price.certFeePence / 36) + Math.ceil(price.annualFeePence / 12) : null;
                          return (
                            <option key={b.id} value={b.id}>
                              {b.name}{b.accreditation ? ` (${b.accreditation})` : ""}{monthly !== null ? ` — £${(monthly / 100).toFixed(2)}/mo` : ""}
                            </option>
                          );
                        })}
                      </select>
                      <button
                        onClick={() => pending && assignBureau(fw, pending)}
                        disabled={savingBureau || !pending}
                        style={{ ...btnPrimary, fontSize: 12, padding: "6px 12px", opacity: savingBureau || !pending ? 0.5 : 1 }}
                      >
                        {engagement ? "Reassign" : "Assign"}
                      </button>
                      {engagement?.locked && (
                        <button
                          onClick={() => releaseLock(fw)}
                          disabled={savingBureau}
                          style={{ background: "none", border: `1px solid ${theme.color.warning}`, borderRadius: 3, padding: "6px 12px", fontSize: 12, color: theme.color.warningText, cursor: "pointer" }}
                        >
                          Release lock
                        </button>
                      )}
                      {engagement && (
                        <button
                          onClick={() => {
                            // Ending an engagement removes the bureau's access for this standard and its lock: ask first.
                            if (!window.confirm(`End the certification engagement for ${FRAMEWORK_LABELS[fw as FrameworkId] ?? fw}? The bureau's auditors lose access to this standard.`)) return;
                            void assignBureau(fw, null);
                          }}
                          disabled={savingBureau}
                          style={{ background: "none", border: `1px solid ${theme.color.danger}`, borderRadius: 3, padding: "6px 12px", fontSize: 12, color: theme.color.dangerText, cursor: "pointer" }}
                        >
                          End engagement
                        </button>
                      )}
                    </div>
                  </div>
                  {pendingBureau && (
                    <div style={{ fontSize: 11, color: theme.color.warningText, marginTop: 8 }}>
                      Assigning {pendingBureau.name} locks {FRAMEWORK_LABELS[fw as FrameworkId] ?? fw} with it for 3 years
                      {pendingPrice ? ` · cert £${(pendingPrice.certFeePence / 100).toFixed(0)} · annual £${(pendingPrice.annualFeePence / 100).toFixed(0)}` : " · no price listed for this standard"}
                      {engagement?.locked ? " · overrides the current lock" : ""}.
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Consultancy bookings */}
      <div style={{ ...card }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: theme.color.heading, marginBottom: 4 }}>Consultancy Bookings (Time & Materials)</div>
        {rateCardError && (
          <div style={{ fontSize: 12, color: theme.color.warningText, background: theme.color.warningBg, borderRadius: 3, padding: "6px 10px", marginBottom: 8 }}>
            Your rate card could not be loaded, so the platform's default rates are shown below. Reload before saving prices.
          </div>
        )}
        <div style={{ fontSize: 12, color: theme.color.subtle, marginBottom: 14 }}>
          Set the monthly allocation for this client, priced at your <strong>Consultant</strong> grade rate from the Rate Card page.
          Consultant time is billed to the client on top of the platform fee. To offer a flat-fee arrangement instead, see <strong>Managed Service Contract</strong> below.
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: theme.color.text }}>Full Days ({pence(tmDayPence)}/day)</div>
              <div style={{ fontSize: 11, color: theme.color.subtle }}>
                {pence(daysPerMonth * tmDayPence)}/mo
              </div>
            </div>
            <Stepper value={daysPerMonth} onChange={setDaysPerMonth} step={0.5} />
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: theme.color.text }}>Ad-hoc Hours ({pence(tmHourPence)}/hr)</div>
              <div style={{ fontSize: 11, color: theme.color.subtle }}>
                {pence(hoursRemaining * tmHourPence)}
              </div>
            </div>
            <Stepper value={hoursRemaining} onChange={setHoursRemaining} />
          </div>

          {consultancyError && <div style={{ fontSize: 12, color: theme.color.dangerText }}>{consultancyError}</div>}
          <button
            onClick={saveConsultancy}
            disabled={savingConsultancy || (daysPerMonth === org.consultancyDaysPerMonth && hoursRemaining === org.consultancyHoursRemaining)}
            style={{ ...btnPrimary, alignSelf: "flex-start", opacity: (savingConsultancy || (daysPerMonth === org.consultancyDaysPerMonth && hoursRemaining === org.consultancyHoursRemaining)) ? 0.5 : 1 }}
          >
            {savingConsultancy ? "Saving…" : "Save bookings"}
          </button>
        </div>
      </div>

      {/* Managed Service Contract */}
      <div style={{ ...card }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: theme.color.heading, marginBottom: 4 }}>Managed Service Contract</div>
        <div style={{ fontSize: 12, color: theme.color.subtle, marginBottom: 16 }}>
          Set a flat monthly fee and an included booking allotment. The client can opt in to this arrangement from their My Services page. If active, their time & materials rates will be hidden and bookings count against the allotment.
          Start from your consultancy-wide offer (set on the Rate Card page) or enter a bespoke fee for this client.
        </div>

        {rateCard && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: theme.color.text, marginBottom: 6 }}>Apply your rate card offer</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {RATE_GRADES.map((g) => {
                const e = rateCard[g];
                if (!e) return null;
                const offered = e.monthPence > 0;
                const active  = msGrade === g;
                return (
                  <button
                    key={g}
                    onClick={() => applyRateCardOffer(g)}
                    disabled={!offered}
                    title={offered ? undefined : "Not offered as a managed service — enable it on the Rate Card page"}
                    style={{
                      background: active ? theme.color.accentBg : theme.color.bg,
                      border: `1px solid ${active ? theme.color.accent : theme.color.border}`,
                      borderRadius: 3, padding: "8px 12px", fontSize: 12, textAlign: "left",
                      cursor: offered ? "pointer" : "not-allowed", opacity: offered ? 1 : 0.5,
                      color: active ? theme.color.accentText : theme.color.text,
                    }}
                  >
                    <div style={{ fontWeight: 700 }}>{e.label}</div>
                    <div style={{ fontSize: 11, color: theme.color.subtle }}>
                      {offered
                        ? `${pence(e.monthPence)}/mo · ${e.managedDays > 0 ? `${e.managedDays}d` : ""}${e.managedDays > 0 && e.managedHours > 0 ? " + " : ""}${e.managedHours > 0 ? `${e.managedHours}h` : ""} included`
                        : "Not offered"}
                    </div>
                  </button>
                );
              })}
              {msGrade && (
                <button
                  onClick={() => setMsGrade(null)}
                  style={{ background: "none", border: `1px solid ${theme.color.border}`, borderRadius: 3, padding: "8px 12px", fontSize: 12, cursor: "pointer", color: theme.color.subtle }}
                >
                  Bespoke fee
                </button>
              )}
            </div>
            {msGrade && <div style={{ fontSize: 11, color: theme.color.faint, marginTop: 6 }}>Editing the fee or allotment below keeps the {rateCard[msGrade]?.label ?? msGrade} grade but records the bespoke values for this client.</div>}
          </div>
        )}

        {org.managedServiceRatePence != null && (
          <div style={{
            background: theme.color.accentBg, border: `1px solid ${theme.color.accent}44`,
            borderRadius: 3, padding: "10px 14px", marginBottom: 14, fontSize: 12,
            color: theme.color.accentText,
          }}>
            Currently active — client is on{" "}
            <strong>{org.billingMode === "managed_service" ? "managed service" : "time & materials"}</strong>.
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Flat fee input */}
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: theme.color.text, marginBottom: 6 }}>Monthly flat fee (£)</div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 16, color: theme.color.muted }}>£</span>
              <input
                type="number"
                min="0"
                step="1"
                value={msFlatFeeInput}
                onChange={(e) => setMsFlatFeeInput(e.target.value)}
                placeholder="e.g. 2000"
                style={{ ...inputStyle, width: 160, fontSize: 16, fontWeight: 700 }}
              />
              <span style={{ fontSize: 12, color: theme.color.subtle }}>/month</span>
            </div>
          </div>

          {/* Included allotment */}
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: theme.color.text, marginBottom: 6 }}>Included allotment</div>
            <div style={{ fontSize: 11, color: theme.color.subtle, marginBottom: 10 }}>
              Days and hours the client can book within the contract. These do not affect the price.
            </div>
            <div style={{ display: "flex", gap: 20 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: theme.color.text, marginBottom: 6 }}>Days / month</div>
                <Stepper value={msDays} onChange={setMsDays} step={0.5} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: theme.color.text, marginBottom: 6 }}>Hours / month</div>
                <Stepper value={msHours} onChange={setMsHours} />
              </div>
            </div>
          </div>

          {/* Summary preview */}
          {msFlatFeePence > 0 && (
            <div style={{ background: theme.color.panelAlt, borderRadius: 3, padding: "10px 14px", fontSize: 13 }}>
              <span style={{ color: theme.color.subtle }}>Monthly contract value: </span>
              <strong style={{ color: theme.color.heading }}>
                £{(msFlatFeePence / 100).toLocaleString("en-GB", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              </strong>
              {(msDays > 0 || msHours > 0) && (
                <span style={{ fontSize: 11, color: theme.color.faint, marginLeft: 8 }}>
                  ({msDays > 0 ? `${msDays} day${msDays !== 1 ? "s" : ""}` : ""}{msDays > 0 && msHours > 0 ? " + " : ""}{msHours > 0 ? `${msHours} hr${msHours !== 1 ? "s" : ""}` : ""} included)
                </span>
              )}
            </div>
          )}

          {msError && <div style={{ fontSize: 12, color: theme.color.dangerText }}>{msError}</div>}

          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={saveManagedService}
              disabled={savingMs}
              style={{ ...btnPrimary, opacity: savingMs ? 0.5 : 1 }}
            >
              {savingMs ? "Saving…" : "Save contract"}
            </button>
            {org.managedServiceRatePence != null && (
              <button
                onClick={clearManagedService}
                disabled={clearingMs}
                style={{
                  background: "none", border: `1px solid ${theme.color.danger}`,
                  borderRadius: 3, padding: "6px 14px", fontSize: 12, cursor: "pointer",
                  color: theme.color.dangerText, fontWeight: 600, opacity: clearingMs ? 0.5 : 1,
                }}
              >
                {clearingMs ? "Removing…" : "Remove contract"}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Legal Obligations — DORA (EU/EEA financial entities only; the server decides, lib/jurisdictions.ts doraApplies) */}
      {(() => {
        if (!org.doraApplies && !org.doraMonitoringEnabled) return null;
        return (
          <div style={{ ...card }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: theme.color.heading, marginBottom: 4 }}>Legal Obligations</div>
            <div style={{ fontSize: 12, color: theme.color.subtle, marginBottom: 14 }}>
              Modules required by regulation for this client's operating jurisdictions.
            </div>
            {doraError && <div style={{ color: theme.color.dangerText, fontSize: 12, marginBottom: 8 }}>{doraError}</div>}
            <AddOnRow
              label="DORA Monitoring"
              description={`ICT incident reporting deadlines, resilience testing schedule, and third-party provider register — required under Regulation (EU) 2022/2554 — ${org.package?.addons.dora ? "included in the " + org.package.name : gbpMo(DORA_MONITORING_ADDON_PENCE)}`}
              enabled={org.doraMonitoringEnabled}
              saving={savingDora}
              onToggle={toggleDora}
            />
          </div>
        );
      })()}

      {/* Optional add-ons */}
      <div style={{ ...card }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: theme.color.heading, marginBottom: 4 }}>Optional Add-ons</div>
        <div style={{ fontSize: 12, color: theme.color.subtle, marginBottom: 14 }}>
          Enable or disable premium add-ons for this client.
        </div>
        {tprmError && <div style={{ color: theme.color.dangerText, fontSize: 12, marginBottom: 8 }}>{tprmError}</div>}
        <AddOnRow
          label="Supplier Risk (TPRM)"
          description={`Third-party risk management module — ${org.package?.addons.tprm ? "included in the " + org.package.name : gbpMo(TPRM_ADDON_PENCE)}`}
          enabled={org.tprmEnabled}
          saving={savingTprm}
          onToggle={toggleTprm}
        />
        {esgError && <div style={{ color: theme.color.dangerText, fontSize: 12, marginTop: 8 }}>{esgError}</div>}
        <AddOnRow
          label="Environment & Sustainability Governance (ESG)"
          description={`ISO 14001:2015 and IWA 48:2024 ESG maturity tracking — ${org.package?.addons.esg ? "included in the " + org.package.name : gbpMo(ESG_ADDON_PENCE)}`}
          enabled={org.esgEnabled}
          saving={savingEsg}
          onToggle={toggleEsg}
          style={{ marginTop: 8 }}
        />
        {org.serviceTier.level < 5 && (
          <>
            {auditorError && <div style={{ color: theme.color.dangerText, fontSize: 12, marginTop: 8 }}>{auditorError}</div>}
            <AddOnRow
              label="Auditor access"
              description={`Engage a certification body and work its findings below Full Service, which includes it — ${gbpMo(AUDITOR_ACCESS_ADDON_PENCE)}`}
              enabled={org.auditorAccessEnabled}
              saving={savingAuditor}
              onToggle={toggleAuditorAccess}
              style={{ marginTop: 8 }}
            />
          </>
        )}
        {legalError && <div style={{ color: theme.color.dangerText, fontSize: 12, marginTop: 8 }}>{legalError}</div>}
        <AddOnRow
          label="Jurisdiction Law & Guidance"
          description={`Legal obligations, regulatory summaries and compliance guidance by operating jurisdiction — ${gbpMo(JURISDICTION_ADDON_PENCE)} per jurisdiction`}
          enabled={org.legalObligationsEnabled}
          saving={savingLegal}
          onToggle={toggleLegal}
          style={{ marginTop: 8 }}
        />
      </div>

      {/* Live billing preview */}
      <div style={{ ...card, border: `1px solid ${billingChanged ? theme.color.accent + "66" : theme.color.border}` }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: theme.color.heading }}>Billing Preview</div>
          {billingChanged && (
            <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 4, background: theme.color.accentBg, color: theme.color.accentText, fontWeight: 600 }}>
              Unsaved changes
            </span>
          )}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
          {[
            [platformLineLabel(org), liveBill.standardsTier],
            ...(liveBill.integrations > 0 ? [["Integrations", liveBill.integrations] as [string, number]] : []),
            ...(liveBill.tprm > 0 ? [["Supplier Risk (TPRM)", liveBill.tprm] as [string, number]] : []),
            ...(liveBill.dora > 0 ? [["DORA Monitoring", liveBill.dora] as [string, number]] : []),
            ...(liveBill.esg > 0 ? [["ESG", liveBill.esg] as [string, number]] : []),
            ...(liveBill.auditorAccess > 0 ? [["Auditor access", liveBill.auditorAccess] as [string, number]] : []),
            ...(liveBill.jur > 0 ? [["Jurisdictions", liveBill.jur] as [string, number]] : []),
            ...(liveBill.hardening > 0 ? [["CIS benchmarks", liveBill.hardening] as [string, number]] : []),
            ["Cert fee (bureau)", liveBill.bureauCert],
            ["Surveillance (bureau)", liveBill.bureauAnnual],
            ["Consultancy (allocation)", liveBill.consultancy],
          ].map(([label, val]) => (
            <div key={label as string} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: theme.color.muted }}>
              <span>{label}</span><span>{pence(val as number)}</span>
            </div>
          ))}
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, fontWeight: 700, color: theme.color.heading, borderTop: `1px solid ${theme.color.border}`, paddingTop: 8, marginTop: 4 }}>
            <span>Monthly total</span>
            <span style={{ color: billingChanged ? theme.color.accentText : theme.color.heading }}>
              {pence(liveBill.total)}
              {billingChanged && <span style={{ fontSize: 11, fontWeight: 400, color: theme.color.faint, marginLeft: 8 }}>was {pence(savedBill.total)}</span>}
            </span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: theme.color.consultant }}>
            <span>Your time (allocation)</span><span>{pence(liveBill.consultancy)}</span>
          </div>
        </div>
      </div>

      {/* AI Frequency (read-only) */}
      <div style={{ ...card }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: theme.color.heading }}>AI Check Frequency</div>
            <div style={{ fontSize: 12, color: theme.color.subtle, marginTop: 2 }}>
              {org.isDemo ? "Automatic checks are switched off for demo organisations" : "Set by client"}
            </div>
          </div>
          <span
            style={{
              fontSize: 12, padding: "5px 14px", borderRadius: 3,
              background: theme.color.accentBg, color: theme.color.accentText, fontWeight: 600,
              opacity: org.isDemo ? 0.5 : 1,
              textDecoration: org.isDemo ? "line-through" : undefined,
            }}
          >
            {org.apiFrequency}
          </span>
        </div>
      </div>

      {/* Monthly Spend Cap (read-only) */}
      <div style={{ ...card }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: theme.color.heading }}>Monthly Spend Cap</div>
            <div style={{ fontSize: 12, color: theme.color.subtle, marginTop: 2 }}>Set by client</div>
          </div>
          <span
            style={{
              fontSize: 12, padding: "5px 14px", borderRadius: 3,
              background: theme.color.panel, color: theme.color.muted, fontWeight: 600,
            }}
          >
            {org.spendCapPence > 0 ? `£${(org.spendCapPence / 100).toFixed(2)}` : "No cap"}
          </span>
        </div>
      </div>

      {/* AI Check Cap — only shown when consultancy is using their own AI */}
      {org.hasConsultancyAi && (
        <AiCheckCapCard orgId={org.id} org={org} onUpdated={() => onRefresh()} />
      )}
    </div>
  );
}

function AiCheckCapCard({ orgId, org, onUpdated }: {
  orgId: string;
  org: { consultancyAiCheckCap: number | null; aiChecksThisPeriod: number; aiCheckPeriodResetsAt: string | null };
  onUpdated: (updated: { consultancyAiCheckCap: number | null }) => void;
}) {
  const [capInput, setCapInput] = useState(
    org.consultancyAiCheckCap !== null ? String(org.consultancyAiCheckCap) : ""
  );
  const [saving, setSaving] = useState(false);
  const [saved,  setSaved]  = useState(false);
  const [error,  setError]  = useState("");

  const used  = org.aiChecksThisPeriod;
  const cap   = org.consultancyAiCheckCap;
  const pct   = cap ? Math.min(100, Math.round((used / cap) * 100)) : 0;
  const resets = org.aiCheckPeriodResetsAt
    ? new Date(org.aiCheckPeriodResetsAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
    : null;

  async function save() {
    setSaving(true); setSaved(false); setError("");
    const parsed = capInput.trim() === "" ? null : parseInt(capInput, 10);
    if (parsed !== null && (isNaN(parsed) || parsed < 1)) {
      setError("Enter a whole number ≥ 1, or leave blank for unlimited"); setSaving(false); return;
    }
    try {
      const res = await api.patch<{ consultancyAiCheckCap: number | null }>(`/consultant/clients/${orgId}/ai-check-cap`, { cap: parsed });
      setSaved(true);
      onUpdated(res);
    } catch (e: unknown) {
      const err = e as { payload?: { error?: string }; message?: string };
      setError(apiErrorMessage(err, err?.message || "Save failed"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ ...card }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: theme.color.heading, marginBottom: 2 }}>
        Monthly AI Check Limit
      </div>
      <div style={{ fontSize: 12, color: theme.color.subtle, marginBottom: 14, lineHeight: 1.5 }}>
        Maximum compliance check runs per calendar month. Applies because this consultancy uses a custom AI provider.
        {resets && ` Resets ${resets}.`}
      </div>

      {/* Usage bar */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: theme.color.subtle, marginBottom: 5 }}>
          <span>{used} used this month</span>
          <span>{cap !== null ? `${cap} limit` : "Unlimited"}</span>
        </div>
        {cap !== null && (
          <RulerProgress value={pct} fill={pct >= 100 ? theme.color.danger : pct >= 80 ? theme.color.warning : theme.color.success} label="AI checks used this month" />
        )}
      </div>

      {/* Cap setter */}
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <input
          type="number"
          min={1}
          value={capInput}
          onChange={(e) => { setCapInput(e.target.value); setSaved(false); }}
          placeholder="Unlimited"
          style={{ ...inputStyle, width: 120 }}
        />
        <button onClick={save} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.7 : 1 }}>
          {saving ? "Saving…" : "Save"}
        </button>
        <button
          onClick={() => { setCapInput(""); setSaved(false); }}
          disabled={saving}
          style={{
            fontSize: 12, padding: "7px 12px", borderRadius: 3, cursor: "pointer",
            background: "transparent", border: `1px solid ${theme.color.border}`, color: theme.color.subtle,
          }}
        >
          Remove limit
        </button>
        {saved  && <span style={{ fontSize: 12, color: theme.color.successText }}>Saved</span>}
        {error  && <span style={{ fontSize: 12, color: theme.color.dangerText }}>{error}</span>}
      </div>
    </div>
  );
}

// ── DocumentsTab ──────────────────────────────────────────────────────────

function DocumentsTab({ orgId }: { orgId: string }) {
  const [docs, setDocs] = useState<ClientDoc[] | null>(null);
  const [error, setError] = useState("");
  const [downloadError, setDownloadError] = useState("");

  useEffect(() => {
    api.get<{ documents: ClientDoc[] }>(`/consultant/clients/${orgId}/documents`)
      .then((d) => setDocs(d.documents))
      .catch(() => setError("Failed to load documents."));
  }, [orgId]);

  async function download(docId: string) {
    setDownloadError("");
    try {
      const { url } = await api.get<{ url: string }>(`/consultant/clients/${orgId}/documents/${docId}/download-url`);
      window.open(url, "_blank", "noopener");
    } catch {
      setDownloadError("Could not generate download link. Please try again.");
    }
  }

  function fmtBytes(n: number) {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(1)} MB`;
  }

  if (error) return <div style={{ color: theme.color.dangerText, fontSize: 13 }}>{error}</div>;
  if (!docs) return <div style={{ color: theme.color.subtle, fontSize: 13 }}>Loading…</div>;

  if (docs.length === 0) {
    return (
      <div style={{ ...emptyFrame, color: theme.color.muted, fontSize: 13, padding: 40 }}>
        No evidence documents uploaded yet.
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {downloadError && <div style={{ fontSize: 12, color: theme.color.dangerText, padding: "4px 0" }}>{downloadError}</div>}
      {docs.map((doc) => (
        <div
          key={doc.id}
          style={{
            ...card,
            display: "flex",
            alignItems: "center",
            gap: 16,
            padding: "14px 18px",
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading }}>
                {doc.fileName}
              </span>
              {doc.version > 1 && (
                <span style={{
                  fontSize: 10, padding: "1px 6px", borderRadius: 4,
                  background: theme.color.accentBg, color: theme.color.accentText, fontWeight: 700,
                }}>
                  v{doc.version}
                </span>
              )}
            </div>
            <div style={{ fontSize: 11, color: theme.color.subtle, marginTop: 3, display: "flex", gap: 12 }}>
              <span>{fmtBytes(doc.sizeBytes)}</span>
              <span>{new Date(doc.uploadedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span>
            </div>
            {doc.controls.length > 0 && (
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
                {doc.controls.map((c) => (
                  <span
                    key={`${c.frameworkId}-${c.controlRef}`}
                    style={{
                      fontSize: 10, padding: "2px 7px", borderRadius: 4,
                      background: theme.color.panel, color: theme.color.accentText,
                      border: `1px solid ${theme.color.border}`, fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {c.controlRef}
                  </span>
                ))}
              </div>
            )}
          </div>
          <button
            onClick={() => download(doc.id)}
            style={{
              background: "none",
              border: `1px solid ${theme.color.borderStrong}`,
              borderRadius: 3,
              padding: "6px 14px",
              fontSize: 12,
              color: theme.color.accentText,
              cursor: "pointer",
              fontWeight: 500,
              flexShrink: 0,
            }}
          >
            Download
          </button>
        </div>
      ))}
    </div>
  );
}

// ── ClausesTab ────────────────────────────────────────────────────────────

function ClausesTab({ orgId, focusControlId }: { orgId: string; focusControlId?: string | null }) {
  const [clauses, setClauses] = useState<ClauseEntry[] | null>(null);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    api.get<{ clauses: ClauseEntry[] }>(`/consultant/clients/${orgId}/clauses`)
      .then((d) => { setError(""); setClauses(d.clauses); })
      .catch(() => setError("Failed to load clauses."));
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  // Opened from Today on one clause (?control=): bring it into view once loaded.
  useEffect(() => {
    if (!focusControlId || !clauses) return;
    document.getElementById(`clause-${focusControlId}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [focusControlId, clauses]);

  if (error) return <div style={{ color: theme.color.dangerText, fontSize: 13 }}>{error}</div>;
  if (!clauses) return <div style={{ color: theme.color.subtle, fontSize: 13 }}>Loading…</div>;

  if (clauses.length === 0) {
    return (
      <div style={{ ...emptyFrame, color: theme.color.muted, fontSize: 13, padding: 40 }}>
        No AI-flagged discrepancies — controls the client marked compliant all have supporting evidence.
      </div>
    );
  }

  const byStatus = {
    approved: clauses.filter((c) => c.status === "approved").length,
    needs_work: clauses.filter((c) => c.status === "needs_work").length,
    observation: clauses.filter((c) => c.status === "observation").length,
    pending: clauses.filter((c) => !c.status).length,
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        {[
          { label: "Approved", count: byStatus.approved, color: theme.color.successText },
          { label: "Needs Work", count: byStatus.needs_work, color: theme.color.dangerText },
          { label: "Observation", count: byStatus.observation, color: theme.color.warningText },
          { label: "Pending", count: byStatus.pending, color: theme.color.faint },
        ].map(({ label, count, color }) => (
          <div key={label} style={{ ...card, flex: 1, minWidth: 100, textAlign: "center", padding: "12px 16px" }}>
            <div style={{ fontSize: 22, fontWeight: 700, color, lineHeight: 1 }}>{count}</div>
            <div style={{ fontSize: 11, color: theme.color.subtle, marginTop: 4 }}>{label}</div>
          </div>
        ))}
      </div>
      {focusControlId && !clauses.some((c) => c.controlId === focusControlId) && (
        <div style={{ fontSize: 12.5, color: theme.color.subtle }}>Clause {displayControlRef(focusControlId)} is not in this list — it may already have been reviewed.</div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {clauses.map((clause) => (
          <div key={clause.controlId} id={`clause-${clause.controlId}`}
            style={clause.controlId === focusControlId ? { outline: `2px solid ${theme.color.accent}`, outlineOffset: 2, borderRadius: theme.radius.lg } : undefined}>
            <ClauseCard clause={clause} orgId={orgId} onSaved={load} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ── ApprovalsTab ──────────────────────────────────────────────────────────

interface SoaApprovalRow {
  control: { id: string; frameworkId: string; kind: string; name: string; description: string };
  entry: SoaApprovalEntry & { applicable: boolean | null; justification: string | null; approvalStatus: string };
}

// The status the client is asking to have approved, in the words the client's pages use.
const STATUS_LABEL: Record<string, string> = {
  COMPLIANT:   "Already in place",
  IN_PROGRESS: "Working on it",
  GAP:         "Need to fix this",
  NOT_STARTED: "Not started yet",
};

const STATUS_TONE: Record<string, StatusTone> = {
  COMPLIANT:   "met",
  IN_PROGRESS: "partial",
  GAP:         "gap",
};

// Before and after side by side: the values a consultant last approved against what the client has asked for.
function ApprovalDiff({ entry }: { entry: SoaApprovalRow["entry"] }) {
  const before = approvedView(entry);
  const never = entry.approvedStatus == null && entry.approvedApplicable == null && entry.approvedJustification == null;
  const col = (title: string, v: { status: string; applicable: boolean | null; justification: string | null }, note?: string) => (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: theme.color.faint, marginBottom: 3 }}>{title}</div>
      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
        <StatusTag tone={STATUS_TONE[v.status] ?? "none"}>{soaStatusName(v.status)}</StatusTag>
        <span style={{ fontSize: 11, color: theme.color.subtle }}>{soaApplicabilityName(v.applicable)}</span>
      </div>
      {v.justification && <div style={{ fontSize: 12, color: theme.color.subtle, fontStyle: "italic", marginTop: 3 }}>"{v.justification}"</div>}
      {note && <div style={{ fontSize: 11, color: theme.color.faint, marginTop: 3 }}>{note}</div>}
    </div>
  );
  return (
    <div style={{ display: "flex", gap: 16 }}>
      {col("Last approved", before, never ? "Nothing approved yet" : undefined)}
      {col("Requested", { status: entry.status, applicable: entry.applicable ?? null, justification: entry.justification })}
    </div>
  );
}

function ApprovalsTab({ orgId, onPendingChange }: { orgId: string; onPendingChange?: (count: number) => void }) {
  const { refresh: refreshNavCounts } = useNavCountsContext();
  const [rows, setRows]     = useState<SoaApprovalRow[] | null>(null);
  const [loadError, setLoadError] = useState("");
  const [actionError, setActionError] = useState("");
  const [requiresApproval, setRequiresApproval] = useState(false);
  const [approving, setApproving] = useState<string | null>(null);
  const [bulkWorking, setBulkWorking] = useState(false);
  const [rejecting, setRejecting] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [rejectBusy, setRejectBusy] = useState(false);

  async function loadSoa() {
    try {
      const data = await api.get<{ soa: SoaApprovalRow[]; requiresApproval: boolean }>(`/consultant/clients/${orgId}/soa`);
      setRows(data.soa);
      setRequiresApproval(data.requiresApproval);
    } catch {
      setLoadError("Failed to load approvals.");
    }
  }

  useEffect(() => { loadSoa(); }, [orgId]);

  // The tab's badge and the sidebar / Today counts follow the list as entries are approved,
  // not only after a reload.
  const pendingNow = rows ? (requiresApproval ? rows.filter((r) => r.entry.approvalStatus === "DRAFT").length : 0) : null;
  const firstCount = useRef(true);
  useEffect(() => {
    if (pendingNow === null) return;
    onPendingChange?.(pendingNow);
    if (firstCount.current) { firstCount.current = false; return; }
    refreshNavCounts();
  }, [pendingNow]); // eslint-disable-line react-hooks/exhaustive-deps

  async function approve(controlId: string) {
    setApproving(controlId); setActionError("");
    try {
      await api.put(`/consultant/clients/${orgId}/soa/${controlId}/approve`, {});
      setRows((prev) => prev && prev.map((r) =>
        r.control.id === controlId ? { ...r, entry: { ...r.entry, approvalStatus: "APPROVED" } } : r
      ));
    } catch {
      setActionError("Failed to approve. Please try again.");
    } finally { setApproving(null); }
  }

  async function reject(controlId: string) {
    if (reason.trim().length < 3) { setActionError("Say why the change is refused; the client sees this reason."); return; }
    setRejectBusy(true); setActionError("");
    try {
      await api.put(`/consultant/clients/${orgId}/soa/${controlId}/reject`, { reason: reason.trim() });
      // The row goes back to its approved values, so reload rather than guess them.
      setRejecting(null); setReason("");
      await loadSoa();
    } catch {
      setActionError("Failed to reject. Please try again.");
    } finally { setRejectBusy(false); }
  }

  async function approveAll() {
    setBulkWorking(true); setActionError("");
    try {
      await api.put(`/consultant/clients/${orgId}/soa/approve-all`, {});
      setRows((prev) => prev && prev.map((r) => ({ ...r, entry: { ...r.entry, approvalStatus: "APPROVED" } })));
    } catch {
      setActionError("Failed to approve all. Please try again.");
    } finally { setBulkWorking(false); }
  }

  if (loadError) return <div style={{ color: theme.color.dangerText, fontSize: 13 }}>{loadError}</div>;
  if (!rows) return <div style={{ padding: 16, color: theme.color.subtle, fontSize: 13 }}>Loading…</div>;

  const pending = rows.filter((r) => r.entry.approvalStatus === "DRAFT");

  if (!requiresApproval) {
    return (
      <div style={{ ...emptyFrame, padding: "32px 20px", color: theme.color.faint, fontSize: 13 }}>
        This organisation's tier does not require consultant approval of SOA entries.
      </div>
    );
  }

  if (pending.length === 0) {
    return (
      <div style={{ ...emptyFrame, padding: "32px 20px", color: theme.color.faint, fontSize: 13 }}>
        ✓ All SOA entries are approved — nothing awaiting review.
      </div>
    );
  }

  // Group pending entries by framework
  const byFramework: Record<string, SoaApprovalRow[]> = {};
  for (const r of pending) {
    (byFramework[r.control.frameworkId] ??= []).push(r);
  }

  return (
    <div>
      {actionError && <div style={{ fontSize: 12, color: theme.color.dangerText, marginBottom: 12 }}>{actionError}</div>}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: theme.color.subtle }}>
          {pending.length} entr{pending.length === 1 ? "y" : "ies"} awaiting your approval before the auditor can see them.
        </div>
        <button
          onClick={approveAll}
          disabled={bulkWorking}
          style={{ padding: "8px 18px", borderRadius: 3, fontSize: 12, fontWeight: 700, cursor: "pointer", border: `1px solid ${theme.color.accent}`, background: theme.color.accentBg, color: theme.color.accentText, opacity: bulkWorking ? 0.6 : 1 }}
        >
          {bulkWorking ? "Approving…" : `Approve all (${pending.length})`}
        </button>
      </div>

      {Object.entries(byFramework).map(([fwId, fwRows]) => (
        <div key={fwId} style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: theme.color.accentText, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            {frameworkLabel(fwId)}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {fwRows.map((row) => (
              <div key={row.control.id} style={{ ...card, display: "flex", alignItems: "flex-start", gap: 14 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                    <span style={{ fontVariantNumeric: "tabular-nums", fontSize: 11, color: theme.color.accentText, fontWeight: 700 }}>{displayControlRef(row.control.id)}</span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: theme.color.heading }}>{displayControlTitle(row.control.id, row.control.name)}</span>
                  </div>
                  <ApprovalDiff entry={row.entry} />
                  {rejecting === row.control.id && (
                    <div style={{ marginTop: 10 }}>
                      <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: theme.color.subtle, marginBottom: 4 }}>Reason for refusing (the client sees this)</label>
                      <textarea
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        maxLength={500}
                        rows={2}
                        style={{ width: "100%", boxSizing: "border-box", fontSize: 12, padding: 6, borderRadius: 3, border: `1px solid ${theme.color.border}`, background: theme.color.panel, color: theme.color.text }}
                      />
                      <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                        <button onClick={() => reject(row.control.id)} disabled={rejectBusy} style={{ padding: "5px 12px", borderRadius: 3, fontSize: 12, fontWeight: 600, cursor: "pointer", border: `1px solid ${theme.color.danger}`, background: theme.color.dangerBg, color: theme.color.dangerText, opacity: rejectBusy ? 0.6 : 1 }}>
                          {rejectBusy ? "…" : "Refuse and restore"}
                        </button>
                        <button onClick={() => { setRejecting(null); setReason(""); }} style={{ padding: "5px 12px", borderRadius: 3, fontSize: 12, cursor: "pointer", border: `1px solid ${theme.color.border}`, background: "transparent", color: theme.color.subtle }}>Cancel</button>
                      </div>
                    </div>
                  )}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6, flexShrink: 0 }}>
                  <button
                    onClick={() => approve(row.control.id)}
                    disabled={approving === row.control.id}
                    style={{ padding: "6px 14px", borderRadius: 3, fontSize: 12, fontWeight: 600, cursor: "pointer", border: `1px solid ${theme.color.accent}`, background: theme.color.accentBg, color: theme.color.accentText, opacity: approving === row.control.id ? 0.6 : 1 }}
                  >
                    {approving === row.control.id ? "…" : "Approve"}
                  </button>
                  <button
                    onClick={() => { setRejecting(row.control.id); setReason(""); setActionError(""); }}
                    disabled={rejecting === row.control.id}
                    style={{ padding: "6px 14px", borderRadius: 3, fontSize: 12, fontWeight: 600, cursor: "pointer", border: `1px solid ${theme.color.border}`, background: "transparent", color: theme.color.dangerText }}
                  >
                    Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── UsersTab ──────────────────────────────────────────────────────────────

interface OrgUser {
  id: string;
  displayName: string;
  email: string;
  isActive: boolean;
  organizationId: string | null;
  jobTitle?: string | null;
}

function UsersTab({ orgId, isDemo }: { orgId: string; isDemo: boolean }) {
  const { user: me } = useCurrentUser();
  // A consultant who can open a demo organisation may view it as any of its people
  // (POST /auth/view-as-persona — the server decides; this only offers the button).
  const canViewAs = isDemo && me?.role === "consultant" && !me?.viewedBy;
  const [viewAsError, setViewAsError] = useState("");
  const [users, setUsers] = useState<OrgUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [addingUser, setAddingUser] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [form, setForm] = useState({ displayName: "", email: "" });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [listError, setListError] = useState("");

  function load() {
    setLoading(true);
    api.get<{ users: OrgUser[] }>("/consultant/users")
      .then((d) => { setUsers(d.users.filter((u) => u.organizationId === orgId)); setListError(""); })
      .catch((e) => setListError(e instanceof Error && e.message ? `Could not load users: ${e.message}` : "Could not load users"))
      .finally(() => setLoading(false));
  }
  useEffect(load, [orgId]);

  function openAdd() {
    setEditingId(null); setAddingUser(true); setForm({ displayName: "", email: "" }); setFormError("");
  }
  function openEdit(u: OrgUser) {
    setAddingUser(false); setEditingId(u.id); setForm({ displayName: u.displayName, email: u.email }); setFormError("");
  }
  function cancelForm() { setEditingId(null); setAddingUser(false); }

  async function saveForm() {
    setSaving(true); setFormError("");
    try {
      if (editingId) {
        // The address is the one the organisation invited and stays theirs (the server refuses a change).
        await api.patch(`/consultant/users/${editingId}`, { displayName: form.displayName });
      } else {
        await api.post("/consultant/users", { displayName: form.displayName, email: form.email, role: "client", organizationId: orgId });
      }
      cancelForm(); load();
    } catch (e: unknown) {
      setFormError(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function reactivate(u: OrgUser) {
    try {
      await api.patch(`/consultant/users/${u.id}`, { isActive: true });
      load();
    } catch (e) {
      setListError(e instanceof Error && e.message ? e.message : `Could not reactivate ${u.displayName}`);
    }
  }

  async function viewAs(u: OrgUser) {
    setViewAsError("");
    try {
      await viewAsPersona(u.id);
    } catch (e) {
      setViewAsError(e instanceof Error && e.message ? e.message : `Could not view as ${u.displayName}`);
    }
  }

  async function confirmDelete(id: string) {
    try {
      await api.del(`/consultant/users/${id}`);
      setDeletingId(null);
      load();
    } catch (e) {
      setDeletingId(null);
      setListError(e instanceof Error && e.message ? e.message : "Could not deactivate the user");
    }
  }

  const userForm = (
    <div style={{ background: theme.color.panel, border: `1px solid ${theme.color.border}`, borderRadius: 3, padding: 16, marginTop: 8, display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading }}>{editingId ? "Edit User" : "Add User"}</div>
      {formError && <div style={{ fontSize: 12, color: theme.color.dangerText }}>{formError}</div>}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <label style={{ fontSize: 11, color: theme.color.subtle }}>Full name</label>
          <input style={inputStyle} value={form.displayName} onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))} placeholder="Jane Smith" />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <label style={{ fontSize: 11, color: theme.color.subtle }}>Email</label>
          <input style={{ ...inputStyle, ...(editingId ? { opacity: 0.7 } : {}) }} type="email" value={form.email} readOnly={!!editingId} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="jane@example.com" />
          {editingId && <span style={{ fontSize: 11, color: theme.color.subtle }}>Their sign-in address cannot be changed. Invite a new address and deactivate this account instead.</span>}
        </div>
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={saveForm} disabled={saving || !form.displayName.trim() || !form.email.trim()} style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}>{saving ? "Saving…" : "Save"}</button>
        <button onClick={cancelForm} style={{ background: "none", border: `1px solid ${theme.color.border}`, borderRadius: 3, padding: "9px 18px", fontSize: 13, color: theme.color.muted, cursor: "pointer" }}>Cancel</button>
      </div>
    </div>
  );

  if (loading) return <div style={{ color: theme.color.subtle, fontSize: 13 }}>Loading users…</div>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontSize: 13, color: theme.color.subtle }}>
          {users.length === 0 ? "No users in this organisation." : (() => {
            // Deactivated people are listed but never counted as members.
            const active = users.filter((u) => u.isActive).length;
            const inactive = users.length - active;
            return `${active} active user${active !== 1 ? "s" : ""}${inactive ? ` · ${inactive} deactivated` : ""}`;
          })()}
        </div>
        <button onClick={openAdd} disabled={addingUser} style={{ ...btnPrimary, fontSize: 12, padding: "6px 14px", opacity: addingUser ? 0.4 : 1 }}>+ Add User</button>
      </div>

      {listError && <div role="alert" style={{ fontSize: 12.5, color: theme.color.dangerText }}>{listError}</div>}
      {viewAsError && <div role="alert" style={{ fontSize: 12.5, color: theme.color.dangerText }}>{viewAsError}</div>}
      {canViewAs && (
        <div style={{ fontSize: 12, color: theme.color.subtle }}>
          Demo organisation: use View as to see the platform exactly as one of these people does — their sidebar, their permissions. Anything you change is saved as theirs.
        </div>
      )}

      {addingUser && userForm}

      {users.length === 0 && !addingUser && !listError && (
        <div style={{ ...emptyFrame, padding: "32px 20px", color: theme.color.faint, fontSize: 13 }}>
          No users yet. Add a user to give them access to this organisation.
        </div>
      )}

      {users.map((u) => {
        const isEditing = editingId === u.id;
        const isDeleting = deletingId === u.id;
        return (
          <div key={u.id} style={{ display: "flex", flexDirection: "column" }}>
            <div style={{
              display: "flex", alignItems: "center", gap: 12, padding: "12px 16px",
              background: theme.color.panel, border: `1px solid ${theme.color.border}`,
              borderRadius: isEditing || isDeleting ? "3px 3px 0 0" : 3,
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: u.isActive ? theme.color.heading : theme.color.subtle }}>
                  {u.displayName}
                  {!u.isActive && <span style={{ marginLeft: 8, fontSize: 10, color: theme.color.faint }}>[deactivated]</span>}
                </div>
                <div style={{ fontSize: 11, color: theme.color.faint }}>{u.jobTitle ? `${u.jobTitle} · ${u.email}` : u.email}</div>
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                {canViewAs && u.isActive && (
                  <button onClick={() => viewAs(u)} title={`See the platform as ${u.displayName} does`}
                    style={{ background: theme.color.accentBg, border: `1px solid ${theme.color.accent}`, borderRadius: 3, padding: "4px 10px", fontSize: 11, color: theme.color.accentText, cursor: "pointer", fontWeight: 600 }}>
                    View as
                  </button>
                )}
                <button onClick={() => openEdit(u)} style={{ background: "none", border: `1px solid ${theme.color.border}`, borderRadius: 3, padding: "4px 10px", fontSize: 11, color: theme.color.muted, cursor: "pointer" }}>Edit</button>
                {u.isActive
                  ? <button onClick={() => setDeletingId(u.id)} style={{ background: "none", border: `1px solid ${theme.color.danger}`, borderRadius: 3, padding: "4px 10px", fontSize: 11, color: theme.color.dangerText, cursor: "pointer" }}>Deactivate</button>
                  : <button onClick={() => reactivate(u)} style={{ background: "none", border: `1px solid ${theme.color.success}`, borderRadius: 3, padding: "4px 10px", fontSize: 11, color: theme.color.successText, cursor: "pointer" }}>Reactivate</button>}
              </div>
            </div>
            {isEditing && <div style={{ background: theme.color.panel, border: `1px solid ${theme.color.border}`, borderTop: "none", borderRadius: "0 0 3px 3px", padding: "0 16px 16px" }}>{userForm}</div>}
            {isDeleting && (
              <div style={{ background: theme.color.dangerBg, border: `1px solid ${theme.color.danger}`, borderTop: "none", borderRadius: "0 0 3px 3px", padding: "12px 16px", display: "flex", alignItems: "center", gap: 12, fontSize: 13 }}>
                <span style={{ color: theme.color.dangerText, flex: 1 }}>Deactivate <strong>{u.displayName}</strong>? They are signed out and can no longer sign in; their work keeps their name, and you can reactivate them later.</span>
                <button onClick={() => confirmDelete(u.id)} style={{ ...btnPrimary, background: theme.color.danger }}>Deactivate</button>
                <button onClick={() => setDeletingId(null)} style={{ background: "none", border: `1px solid ${theme.color.border}`, borderRadius: 3, padding: "6px 12px", fontSize: 12, color: theme.color.muted, cursor: "pointer" }}>Cancel</button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── NextTab ───────────────────────────────────────────────────────────────
// What a client needs, first: the consultant's own work for this client (from
// the portfolio list, GET /consultant/actions) and the client's own What's next
// list (GET /actions for this organisation), each ranked most urgent first.
// Opening a client lands here rather than on the profile.

const CLIENT_VIEW_TABS = ["next", "overview", "services", "clauses", "documents", "approvals", "notes", "users"] as const;
type ClientViewTab = (typeof CLIENT_VIEW_TABS)[number];

interface NextItem {
  id: string;
  type: string;
  title: string;
  description: string;
  href: string;
  severity: "high" | "medium" | "low";
  dueAt: string | null;
}

/** Consultant action types that are answered on a tab of this view rather than elsewhere. */
const ACTION_TAB: Record<string, ClientViewTab> = {
  clause_review: "clauses",
  soa_approval: "approvals",
  tier_request: "services",
  sector_unresolved: "overview",
  follow_up: "notes",
};

const NEXT_SEVERITY: Record<string, { bg: string; text: string; label: string }> = {
  high:   { bg: "var(--c-dangerBg)",  text: "var(--c-dangerText)",  label: "Urgent" },
  medium: { bg: "var(--c-warningBg)", text: "var(--c-warningText)", label: "Action" },
  low:    { bg: "var(--c-accentBg)",  text: "var(--c-accentText)",  label: "Review" },
};

function NextList({ title, empty, items, onOpen }: { title: string; empty: string; items: NextItem[] | null; onOpen: (item: NextItem) => void }) {
  return (
    <div style={{ ...card, padding: 0, overflow: "hidden", marginBottom: 16 }}>
      <div style={{ padding: "12px 18px", fontSize: 13.5, fontWeight: 700, color: theme.color.heading, display: "flex", gap: 8, alignItems: "center" }}>
        {title}
        {items && items.length > 0 && <span style={{ fontSize: 11, fontWeight: 700, background: "var(--c-warningBg)", color: "var(--c-warningText)", borderRadius: 3, padding: "1px 8px" }}>{items.length}</span>}
      </div>
      {items === null && <div style={{ padding: "0 18px 14px", fontSize: 12.5, color: theme.color.subtle }}>Loading...</div>}
      {items && items.length === 0 && (
        <div style={{ padding: "0 18px 14px", fontSize: 12.5, color: theme.color.subtle, display: "flex", alignItems: "center", gap: 6 }}>
          <CheckCircle2 size={14} color={theme.color.success} /> {empty}
        </div>
      )}
      {items && items.map((item) => {
        const sev = NEXT_SEVERITY[item.severity] ?? NEXT_SEVERITY.low;
        return (
          <button
            key={item.id}
            type="button"
            onClick={() => onOpen(item)}
            style={{ display: "flex", alignItems: "flex-start", gap: 12, width: "100%", padding: "12px 18px", border: "none", borderTop: "1px solid var(--c-border)", background: "transparent", cursor: "pointer", textAlign: "left", fontFamily: theme.font }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "var(--c-panelAlt)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
          >
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", background: sev.bg, color: sev.text, borderRadius: 3, padding: "3px 7px", flexShrink: 0, marginTop: 1 }}>{sev.label}</span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: "block", fontSize: 13, fontWeight: 600, color: theme.color.heading }}>{item.title}</span>
              <span style={{ display: "block", fontSize: 12, color: theme.color.subtle, lineHeight: 1.5 }}>{item.description}</span>
              {item.dueAt && <span style={{ display: "block", fontSize: 11, color: theme.color.faint, marginTop: 3 }}>Due {fmtDate(item.dueAt)}</span>}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function NextTab({ orgId, onTab }: { orgId: string; onTab: (tab: ClientViewTab) => void }) {
  const navigate = useNavigate();
  const [mine, setMine] = useState<NextItem[] | null>(null);
  const [theirs, setTheirs] = useState<NextItem[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    // Narrowed on the server, so booking requests and events (which link to the
    // calendar) are included and the firm-wide clause cap cannot hide this client's.
    api.get<{ items: NextItem[] }>("/consultant/actions", { organizationId: orgId })
      .then((d) => { if (!cancelled) setMine(rankActions(d.items)); })
      .catch(() => { if (!cancelled) setMine([]); });
    api.get<{ items: NextItem[] }>(`/actions?organizationId=${encodeURIComponent(orgId)}`)
      .then((d) => { if (!cancelled) setTheirs(rankActions(d.items)); })
      .catch(() => { if (!cancelled) setTheirs([]); });
    return () => { cancelled = true; };
  }, [orgId]);

  return (
    <div>
      <NextList
        title="For you to do"
        empty="Nothing waiting on you for this client."
        items={mine}
        onOpen={(item) => {
          // Items carry a deep link (?tab=clauses&control=…, the calendar day, the
          // read-through page); the tab map is the fallback for any that do not.
          if (item.href.startsWith("/consultant/")) navigate(item.href);
          else if (ACTION_TAB[item.type]) onTab(ACTION_TAB[item.type]);
        }}
      />
      <NextList
        title="The client's to-do list"
        empty="The client is up to date."
        items={theirs}
        // The client's own paths are client-shell relative: open them in the read-through.
        onOpen={(item) => { if (item.href.startsWith("/")) navigate(`${clientPortalPath(orgId)}${item.href}`); }}
      />
    </div>
  );
}

// ── Main ConsultantClientView ─────────────────────────────────────────────

interface Props {
  orgId: string;
  onBack: () => void;
  onViewAsClient?: () => void;
}

export function ConsultantClientView({ orgId, onBack, onViewAsClient }: Props) {
  const navigate = useNavigate();
  const [org, setOrg] = useState<ClientDetail | null>(null);
  // The tab lives in the URL (?tab=, and ?control= for one clause) so a tab can be
  // linked to and survives a refresh. Opening a client lands on what needs doing.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const activeTab: ClientViewTab = (CLIENT_VIEW_TABS as readonly string[]).includes(tabParam ?? "") ? (tabParam as ClientViewTab) : "next";
  const focusControlId = searchParams.get("control");
  const setActiveTab = useCallback((t: ClientViewTab) => {
    const next = new URLSearchParams(searchParams);
    if (t === "next") next.delete("tab"); else next.set("tab", t);
    next.delete("control");
    setSearchParams(next);
  }, [searchParams, setSearchParams]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadOrg = useCallback(() => {
    return api.get<ClientDetail>(`/consultant/clients/${orgId}`)
      .then(setOrg)
      .catch(() => setError("Failed to load client."));
  }, [orgId]);

  const loadPendingCount = useCallback(() => {
    return api.get<{ soa: Array<{ entry: { approvalStatus: string } }>; requiresApproval: boolean }>(`/consultant/clients/${orgId}/soa`)
      .then((d) => d.requiresApproval ? setPendingCount(d.soa.filter((r) => r.entry.approvalStatus === "DRAFT").length) : setPendingCount(0))
      .catch(() => {});
  }, [orgId]);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadOrg(), loadPendingCount()]).finally(() => setLoading(false));
  }, [loadOrg, loadPendingCount]);

  if (loading) {
    return (
      <div style={{ padding: 40, color: theme.color.subtle, fontSize: 13, fontFamily: theme.font }}>
        Loading client…
      </div>
    );
  }

  if (error || !org) {
    return (
      <div style={{ padding: 40, fontFamily: theme.font }}>
        <div style={{ color: theme.color.dangerText, fontSize: 13, marginBottom: 16 }}>
          {error || "Client not found."}
        </div>
        <button onClick={onBack} style={{ ...btnPrimary }}>← All clients</button>
      </div>
    );
  }

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", fontFamily: theme.font, overflow: "auto" }}>
      {/* Client banner */}
      <div
        style={{
          background: theme.color.panel, borderBottom: `1px solid ${theme.color.border}`,
          padding: "20px 32px",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ fontSize: 22, fontWeight: 800, color: theme.color.heading }}>{org.name}</div>
            <div style={{ fontSize: 12, color: theme.color.subtle, marginTop: 4 }}>
              {[org.sector, geographyLabel(org.geography), org.size ? `${org.size} staff` : null, org.ciso ? `CISO: ${org.ciso}` : null]
                .filter(Boolean).join(" · ") || "No profile details"}
            </div>
            {org.frameworks.length > 0 && (
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
                {org.frameworks.map((f) => {
                  const fw = ALL_FRAMEWORKS.find((x) => x.id === f.frameworkId);
                  return fw ? (
                    <span
                      key={f.frameworkId}
                      style={{
                        fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 3,
                        background: theme.color.accentBg, color: theme.color.accentText,
                        border: `1px solid ${theme.color.accent}55`,
                      }}
                    >
                      {fw.name}
                    </span>
                  ) : null;
                })}
              </div>
            )}
          </div>
          <div style={{ textAlign: "right", display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
            <div style={{ fontSize: 36, fontWeight: 800, color: scoreColor(org.latestScore), lineHeight: 1 }}>
              {org.latestScore !== null ? `${org.latestScore}%` : "—"}
            </div>
            <div style={{ fontSize: 11, color: theme.color.subtle }}>SOA score · {scoreLabel(org.latestScore)}</div>
            <div style={{ display: "flex", gap: 12, fontSize: 11, justifyContent: "flex-end" }}>
              <span style={{ color: theme.color.successText }}>{org.soaStats.compliant} compliant</span>
              <span style={{ color: theme.color.warningText }}>{org.soaStats.partial} partial</span>
              <span style={{ color: theme.color.dangerText }}>{org.soaStats.gap} gaps</span>
            </div>
            {onViewAsClient && (
              <button
                onClick={onViewAsClient}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  background: "none", border: `1px solid ${theme.color.borderStrong}`,
                  borderRadius: 3, padding: "5px 12px", fontSize: 11,
                  color: theme.color.subtle, cursor: "pointer", marginTop: 2,
                  transition: "color 0.1s, border-color 0.1s",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.color = theme.color.text; e.currentTarget.style.borderColor = theme.color.accent; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = theme.color.subtle; e.currentTarget.style.borderColor = theme.color.borderStrong; }}
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M7 1h4v4M11 1L6.5 5.5M5 2H2v8h8V7" />
                </svg>
                View as client
              </button>
            )}
            <ViewAsPersonaPicker orgId={org.id} isDemo={org.isDemo ?? /\(Demo\)\s*$/i.test(org.name)} />
            {/* Both reports are the client's own page, opened in the read-through portal so they
                are generated and downloaded for this organisation (ExecutiveReport.tsx). */}
            <div style={{ display: "flex", gap: 6, marginTop: 2 }}>
              <button
                onClick={() => navigate(`${clientPortalPath(orgId)}/executive-report`)}
                style={{ ...btnSecondary, padding: "5px 12px", fontSize: 11 }}
                title="Generate or download this client's board report"
              >
                Board report
              </button>
              <button
                onClick={() => navigate(`${clientPortalPath(orgId)}/executive-report?tab=gaps`)}
                style={{ ...btnSecondary, padding: "5px 12px", fontSize: 11 }}
                title="Download this client's gap analysis PDF"
              >
                Gap analysis
              </button>
            </div>
          </div>
        </div>

        <ClientSummaryStrip client={org} />

        {/* Tabs. What's next has none: it is the client's own "What's next" sidebar row
            (nav/consultantNav.ts clientOverviewPath), where plain /consultant/clients/:id lands. */}
        <IndexTabs<ClientViewTab>
          items={[
            { id: "overview", label: "Profile" },
            { id: "services", label: "Billing & Contract" },
            { id: "clauses", label: "Review" },
            { id: "documents", label: "Documents" },
            { id: "approvals", label: "SOA Approvals", count: pendingCount > 0 ? pendingCount : undefined },
            { id: "notes", label: "Notes" },
            { id: "users", label: "Users" },
          ]}
          value={activeTab}
          onChange={setActiveTab}
          ground="var(--c-panel)"
          style={{ marginTop: 20 }}
        />
      </div>

      {/* Tab content */}
      <div style={{ flex: 1, padding: "20px 24px", width: "100%", boxSizing: "border-box" }}>
        {activeTab === "next"      && <NextTab orgId={org.id} onTab={setActiveTab} />}
        {activeTab === "overview"  && <OverviewTab org={org} onRefresh={loadOrg} />}
        {activeTab === "services"  && <ServicesTab org={org} onRefresh={loadOrg} />}
        {activeTab === "clauses"   && <ClausesTab orgId={org.id} focusControlId={focusControlId} />}
        {activeTab === "documents" && <DocumentsTab orgId={org.id} />}
        {activeTab === "approvals" && <ApprovalsTab orgId={org.id} onPendingChange={setPendingCount} />}
        {activeTab === "notes"     && <EngagementNotesTab orgId={org.id} />}
        {activeTab === "users"     && <UsersTab orgId={org.id} isDemo={org.isDemo ?? /\(Demo\)\s*$/i.test(org.name)} />}
      </div>
    </div>
  );
}
